// ===========================================
// BUSTED! BLACKJACK - USE GAME EVENTS HOOK
// ===========================================
// Connects socket events to animation queue and game state stores
// ===========================================

import { useEffect, useCallback, useRef } from 'react';
import { useSocketStore, useAuthStore } from '../stores';
import { useAnimationQueueStore } from '../stores/animationQueueStore';
import { useAnimationConfigStore } from '../stores/animationConfigStore';
import { useGameStateStore } from '../stores/gameStateStore';
import { useCardTrackingStore } from '../stores/cardTrackingStore';
import { getSeatCarouselStore } from '../stores/seatCarouselStore';
import { createWebAnimationRenderer } from '../renderers';
import type { BlackjackEvent, Card, KeyedBatch, ScorecardHandResult, ScorecardSidebetResult, ScorecardInsuranceResult, ChipBreakdown } from '@busted-blackjack/shared';
import { GamePhase, PlayerAction } from '@busted-blackjack/shared';
import { getClientTableMaster } from '../utils/ClientTableMaster';
// PIXI PURITY: All DOM-based card animation functions removed - Pixi handles all card animations
// import { captureAllPlayerCards, captureSingleSplitHandClones, captureOpponentSplitHandCards, captureDealerCards, rakeStoredCards, clearStoredCards, clearArrivalClonesForSeat, animateSplitCardSlide, straightenArrivalClones, ensureClonesExist, ensureAllClonesExist, hideAllReactCardsWithClones } from '../utils/cardAnimations';
import { recordScoreSnapshot, clearAllScoreHistory, incrementRoundAndLogDiagnostics, getAnimationOrchestrator } from '../components/game/pixi/PixiGameTableV1';
import { getSeatRotation } from './useSeatRotation';
import { recordBatchTimeout } from '../stores/adminLogStore';
// NOTE: isDormant import removed - we no longer use dormant state (socket stays alive when tab hidden)
// PIXI PURITY: All DOM-based opponent chip animations removed - Pixi handles all chip animations
// import { animateOpponentBetToKitty, animateOpponentSplitBetToKitty, animateOpponentInsuranceToKitty, animateOpponentInsuranceLossToDealer, animateOpponentInsuranceKittyToAvatar, animatePayoutToOpponentKitty, animateOpponentKittyToAvatar, animateOpponentLossToDealer, breakdownChips } from '../utils/chipAnimations';
import { breakdownChips } from '../utils/chipAnimations';
// NOTE: captureKittyChips import removed - unified chip clones not used by old animation system
import { playSound } from '../utils/animationRunner';
import { diagLog } from '../utils/diagnosticLogger';

// ===========================================
// SEAT RESOLUTION EVENT (Single event for all hand outcomes)
// ===========================================
export interface SeatResolutionHand {
  handIndex: number;
  outcome: 'win' | 'lose' | 'push' | 'bust' | 'blackjack' | 'surrender';
  payout?: number;      // Amount won (for wins)
  collected?: number;   // Amount lost (for losses)
  returned?: number;    // Amount returned (for push)
}

export interface SeatResolutionEvent {
  seatPosition: number;
  hands: SeatResolutionHand[];
}

interface UseGameEventsOptions {
  tableId: string;
  /** duration is the server-provided animation duration from event.duration (undefined if not set) */
  onCardDealt?: (target: 'player' | 'dealer', seatPosition: number | undefined, handIndex: number, cardIndex: number, card: Card, duration?: number) => Promise<void>;
  onHandBusted?: (seatPosition: number, handIndex: number) => Promise<void>;
  onHandBlackjack?: (seatPosition: number, handIndex: number) => Promise<void>;
  /** flipDuration is the server-provided card flip animation duration */
  onDealerRevealsHole?: (flipDuration?: number) => Promise<void>;
  onDealerBlackjack?: () => Promise<void>;
  onDealerStands?: () => Promise<void>;
  onDealerBusts?: () => Promise<void>;
  onChipsPaid?: (seatPosition: number, handIndex: number, amount: number, duration?: number) => Promise<void>;
  onChipsCollected?: (seatPosition: number, handIndex: number, amount: number, duration?: number) => Promise<void>;
  onChipsReturned?: (seatPosition: number, handIndex: number, amount: number) => Promise<void>;
  /**
   * Called when SEAT_RESOLUTION event is received - contains all hand outcomes for a seat.
   * Frontend iterates hands in REVERSE order (4→3→2→1) for visual presentation.
   * Replaces individual CHIPS_PAID/COLLECTED/RETURNED events for coordinated split hand resolution.
   */
  onSeatResolution?: (event: SeatResolutionEvent) => Promise<void>;
  onRoundStarted?: (roundNumber: number, buttonPosition: number) => Promise<void>;
  onInsuranceAccepted?: (seatPosition: number, amount: number) => Promise<void>;
  onInsuranceResolved?: (seatPosition: number, dealerHasBlackjack: boolean, payoutAmount: number, insuranceBet: number) => Promise<void>;
  /** duration is the server-provided animation duration from event.duration (undefined if not set) */
  onDealerHits?: (cardIndex: number, duration?: number) => Promise<void>;
  onDoubleDown?: (seatPosition: number, handIndex: number, additionalBet: number) => Promise<void>;
  onSplit?: (seatPosition: number, originalHandIndex: number, newHandIndex: number, additionalBet: number) => Promise<void>;
  onSurrender?: (seatPosition: number, handIndex: number, betAmount: number, refundAmount: number) => Promise<void>;
  onMidRoundBust?: (seatPosition: number, handIndex: number, betAmount: number) => Promise<void>;
  onAllResolutionsComplete?: () => Promise<void>;
  /** Called when a player sits out (timeout) - use to rake their cards */
  onPlayerSitOut?: (seatPosition: number) => Promise<void>;
  /** Called before DEALING_STARTED is processed - use to force-rake remaining cards */
  onDealingStarted?: () => Promise<void>;
  /** Called before PLAYER_TURNS_STARTED - use to straighten dealer hand before turn beam activates */
  onPlayerTurnsStarted?: () => Promise<void>;
  /** Called when INSURANCE_OFFERED fires - use to straighten dealer hand before insurance panel shows */
  onInsuranceOffered?: () => Promise<void>;
  /** Called when server commands to rake a seat before bet confirmation - clears previous round's cards */
  onSeatRake?: (seatPosition: number) => Promise<void>;
  /** Called when server commands to rake dealer cards - fires after ALL players have raked */
  onDealerRake?: () => Promise<void>;
  /** Called when current user's bet is confirmed - use for chip animation (rake already done via SEAT_RAKE) */
  onBetConfirmed?: (seatPosition: number, amount: number, betChips: number[]) => Promise<void>;
  /** Called when opponent's bet is confirmed - use for Pixi-based kitty display (DOM animation doesn't work in Pixi) */
  onOpponentBetConfirmed?: (seatPosition: number, amount: number, betChips: number[]) => Promise<void>;
  /** Called for opponent win payouts - chips from dealer to opponent kitty (Pixi) */
  onOpponentPayoutToKitty?: (seatPosition: number, chips: number[]) => Promise<void>;
  /** Called for opponent win/push collection - chips from kitty to avatar (Pixi) */
  onOpponentKittyToAvatar?: (seatPosition: number, chips: number[]) => Promise<void>;
  /** Called for opponent loss - chips from kitty to dealer (Pixi) */
  onOpponentLossToDealer?: (seatPosition: number, chips: number[]) => Promise<void>;
  /** IMPERATIVE: Show outcome label and return promise that resolves when animation completes.
   *  Bypasses React render cycle for event-driven sequencing. */
  onShowOutcomeLabel?: (seatPosition: number, handIndex: number, outcome: string) => Promise<void>;
  /** @deprecated PIXI PURITY: DOM clone capture removed - this option is no longer used */
  useArrivalClones?: boolean;
  /** Called during ROUND_COMPLETED to verify PixiJS scene integrity.
   *  Returns report of issues found - log for debugging purposes. */
  onEnsurePixiSceneHealth?: () => { checked: number; fixed: number; issues: string[] } | null;
  /** Called during mid-hand join to sync dealt card counts and scores so scoreboxes display correctly */
  onSyncDealtCounts?: (
    seatCardCounts: Record<number, number[]>,
    dealerCardCount: number,
    seatScores?: Record<number, Array<{ score: number; isSoft: boolean }>>,
    dealerScore?: { score: number; isSoft: boolean }
  ) => void;
  /** Called when a sidebet is placed. duration/stagger are server-provided animation values */
  onSidebetPlaced?: (seatPosition: number, sidebetType: string, amount: number, rounds: number, duration?: number, stagger?: number) => void;
  /** Called when a sidebet is resolved (win/loss determined) */
  onSidebetResolved?: (seatPosition: number, sidebetType: string, outcome: 'win' | 'lose', winType?: string, payout?: number) => void;
  /** Called when sidebet chips are paid out (win). chipBreakdown is server-authoritative chip breakdown. isFinal=true means icon should retire after animation */
  onSidebetChipsPaid?: (seatPosition: number, sidebetType: string, amount: number, chipBreakdown: ChipBreakdown, duration?: number, isFinal?: boolean) => Promise<void>;
  /** Called when sidebet chips are collected (loss). duration/stagger are server-provided animation values. isFinal=true means icon should retire after animation */
  onSidebetChipsCollected?: (seatPosition: number, sidebetType: string, amount: number, duration?: number, stagger?: number, isFinal?: boolean) => Promise<void>;
  /** Called when a sidebet is removed (terminated by user or expired) */
  onSidebetRemoved?: (seatPosition: number, sidebetType: string) => void;
  /** Called after accepting a keyed resync - use to rebuild Pixi visuals from fresh state */
  onKeyedResyncAccepted?: () => void;
  /** Called when opponent straightens their cards (Pixi-based straightening for Pixi table) */
  onOpponentHandStraightened?: (seatPosition: number, handIndex: number) => Promise<void>;

  // ===========================================
  // PIXI-ONLY OPPONENT ANIMATIONS
  // ===========================================
  // These callbacks ensure ALL opponent animations are Pixi-based.
  // No DOM fallbacks - PixiTable is the only table implementation.

  /** Called when opponent's seat needs to be raked (clear cards from previous round) */
  onOpponentSeatRake?: (seatPosition: number) => Promise<void>;
  /** Called when opponent accepts insurance - animate chips to insurance kitty */
  onOpponentInsuranceAccepted?: (seatPosition: number, amount: number, chips: number[]) => Promise<void>;
  /** Called when opponent's insurance resolves - animate win (to avatar) or loss (to dealer) */
  onOpponentInsuranceResolved?: (seatPosition: number, dealerHasBlackjack: boolean, insuranceChips: number[], payoutChips: number[]) => Promise<void>;
  /** Called when opponent doubles down - animate additional bet chips to kitty */
  onOpponentDoubleDown?: (seatPosition: number, handIndex: number, chips: number[]) => Promise<void>;
  /** Called when opponent splits - animate additional bet chips to new hand's kitty */
  onOpponentSplit?: (seatPosition: number, newHandIndex: number, chips: number[]) => Promise<void>;
  /** Called when opponent surrenders - animate half to dealer, half to avatar */
  onOpponentSurrender?: (seatPosition: number, handIndex: number, lossChips: number[], refundChips: number[]) => Promise<void>;
  /** Called when any player makes an insurance decision (accept or decline) - hide their badge immediately */
  onInsuranceDecision?: (seatPosition: number) => void;
  /** Called when the active hand changes for split scenarios - dims non-active hands */
  onActiveHandChange?: (seatPosition: number, handIndex: number | null) => void;

  // ===========================================
  // SCORECARD CALLBACKS
  // ===========================================
  /**
   * Called when SCORECARD_UPDATE event is received from server.
   * Server-driven scorecard - client just displays data, no calculation needed.
   * Only received by the current player (not broadcast to opponents).
   */
  onScorecardUpdate?: (
    hands: ScorecardHandResult[],
    sidebets: ScorecardSidebetResult[],
    insurance: ScorecardInsuranceResult | undefined,
    totalNetResult: number,
    newBalance: number
  ) => Promise<void>;
}

export function useGameEvents(options: UseGameEventsOptions) {
  const { tableId } = options;
  const socket = useSocketStore((state) => state.socket);
  const rendererRef = useRef<ReturnType<typeof createWebAnimationRenderer> | null>(null);

  // Serial processing queue: prevents concurrent batch processing from interleaving.
  // Without this, if batch 2 (new round) arrives while batch 1 (old round) is awaiting
  // chip animations, both IIFEs run concurrently. Batch 1's ROUND_COMPLETED can then
  // overwrite batch 2's state (phase, currentTurnSeat, availableActions), freezing buttons.
  const processingQueueRef = useRef<Promise<void>>(Promise.resolve());

  // Track whether any sidebets were placed this round (reset at DEALING_STARTED)
  // Used for orchestrator.waitForTypeWithTimeout decision
  const sidebetsPlacedThisRoundRef = useRef<number>(0);

  // Track live-removed sidebets that need their icons retired at round boundary
  // When a LIVE sidebet is removed, we keep the icon for the current hand.
  // At ROUND_COMPLETED, we destroy these icons (they rode to conclusion).
  const sidebetsPendingRetirementRef = useRef<Set<string>>(new Set());

  // Track current batch processing state for timeout logging
  const batchProcessingStateRef = useRef<{
    eventCount: number;
    eventTypes: string[];
    startTime: number;
    calculatedTimeout: number;
    lastProcessedEvent: string | null;
    lastProcessedIndex: number;
  } | null>(null);

  // ===========================================
  // RECONNECTION SAFETY NETS (DEPRECATION CANDIDATE)
  // ===========================================
  // These guards are SAFETY NETS for edge cases (reconnection, network hiccups).
  // ServerTableMaster is the single source of truth for event ordering.
  //
  // IMPORTANT: With the client-driven autoplay architecture (ACTION_RECOMMENDATION),
  // these guards should VERY RARELY fire. The server no longer uses setTimeout to
  // execute autoplay actions - it sends recommendations and waits for client response.
  // This creates natural backpressure that prevents batch superseding.
  //
  // If these guards fire frequently during autoplay, it indicates a bug in the
  // recommendation/response flow - investigate the server-side emitActionRecommendation
  // and client-side ACTION_RECOMMENDATION handler.
  //
  // These guards may still be useful for:
  // - Network latency causing out-of-order delivery
  // - Reconnection scenarios
  // - General resilience
  //
  // Consider removing after the new architecture is proven stable in production.

  // Track processed batch keys to prevent duplicate batch processing.
  // Key format: "round-batchSequence" (e.g., "11-3")
  // Cleared when a new round starts (round number increases).
  // SAFETY NET: Catches duplicate batches from network retries or reconnection.
  const processedBatchesRef = useRef<{ keys: Set<string>; lastRound: number }>({
    keys: new Set(),
    lastRound: 0
  });

  // Track the currently-active batch being processed.
  // When a timeout occurs and queue is reset, a new batch may start processing
  // while the old batch is still running. The old batch checks this ref to detect
  // it has been superseded and should abort remaining events.
  // Format: "round-batchSequence" or null if no batch is active.
  // SAFETY NET: Catches late-arriving batches after timeout/reconnection.
  // DEPRECATION NOTE: With client-driven autoplay, this should rarely be needed.
  const activeBatchRef = useRef<string | null>(null);

  // Track the round number of the last active batch for sequence reset detection.
  // When a batch from a new round becomes active, we reset lastEventSequence to 0.
  // This prevents events from the new round being incorrectly skipped because
  // their sequence numbers are lower than the previous round's lastEventSequence.
  const lastActiveBatchRoundRef = useRef<number>(0);

  // Store callbacks in refs so they're accessible in handleGameEvents without causing re-renders
  const onChipsPaidRef = useRef(options.onChipsPaid);
  const onChipsCollectedRef = useRef(options.onChipsCollected);
  const onChipsReturnedRef = useRef(options.onChipsReturned);
  const onSeatResolutionRef = useRef(options.onSeatResolution);
  const onInsuranceAcceptedRef = useRef(options.onInsuranceAccepted);
  const onInsuranceResolvedRef = useRef(options.onInsuranceResolved);
  const onDoubleDownRef = useRef(options.onDoubleDown);
  const onSplitRef = useRef(options.onSplit);
  const onSurrenderRef = useRef(options.onSurrender);
  const onCardDealtRef = useRef(options.onCardDealt);
  const onDealerHitsRef = useRef(options.onDealerHits);
  const onDealerRevealsHoleRef = useRef(options.onDealerRevealsHole);
  const onDealerBlackjackRef = useRef(options.onDealerBlackjack);
  const onDealerStandsRef = useRef(options.onDealerStands);
  const onDealerBustsRef = useRef(options.onDealerBusts);
  const onMidRoundBustRef = useRef(options.onMidRoundBust);
  const onAllResolutionsCompleteRef = useRef(options.onAllResolutionsComplete);
  const onPlayerSitOutRef = useRef(options.onPlayerSitOut);
  const onDealingStartedRef = useRef(options.onDealingStarted);
  const onPlayerTurnsStartedRef = useRef(options.onPlayerTurnsStarted);
  const onInsuranceOfferedRef = useRef(options.onInsuranceOffered);
  const onSeatRakeRef = useRef(options.onSeatRake);
  const onDealerRakeRef = useRef(options.onDealerRake);
  const onBetConfirmedRef = useRef(options.onBetConfirmed);
  const onOpponentBetConfirmedRef = useRef(options.onOpponentBetConfirmed);
  const onOpponentPayoutToKittyRef = useRef(options.onOpponentPayoutToKitty);
  const onOpponentKittyToAvatarRef = useRef(options.onOpponentKittyToAvatar);
  const onOpponentLossToDealerRef = useRef(options.onOpponentLossToDealer);
  const onShowOutcomeLabelRef = useRef(options.onShowOutcomeLabel);
  const onEnsurePixiSceneHealthRef = useRef(options.onEnsurePixiSceneHealth);
  const onSyncDealtCountsRef = useRef(options.onSyncDealtCounts);
  const onSidebetPlacedRef = useRef(options.onSidebetPlaced);
  const onSidebetResolvedRef = useRef(options.onSidebetResolved);
  const onSidebetChipsPaidRef = useRef(options.onSidebetChipsPaid);
  const onSidebetChipsCollectedRef = useRef(options.onSidebetChipsCollected);
  const onSidebetRemovedRef = useRef(options.onSidebetRemoved);
  const onKeyedResyncAcceptedRef = useRef(options.onKeyedResyncAccepted);
  const onOpponentHandStraightenedRef = useRef(options.onOpponentHandStraightened);
  // PIXI-ONLY opponent animation refs
  const onOpponentSeatRakeRef = useRef(options.onOpponentSeatRake);
  const onOpponentInsuranceAcceptedRef = useRef(options.onOpponentInsuranceAccepted);
  const onOpponentInsuranceResolvedRef = useRef(options.onOpponentInsuranceResolved);
  const onOpponentDoubleDownRef = useRef(options.onOpponentDoubleDown);
  const onOpponentSplitRef = useRef(options.onOpponentSplit);
  const onOpponentSurrenderRef = useRef(options.onOpponentSurrender);
  const onInsuranceDecisionRef = useRef(options.onInsuranceDecision);
  const onActiveHandChangeRef = useRef(options.onActiveHandChange);
  const onScorecardUpdateRef = useRef(options.onScorecardUpdate);
  onCardDealtRef.current = options.onCardDealt;
  onChipsPaidRef.current = options.onChipsPaid;
  onChipsCollectedRef.current = options.onChipsCollected;
  onChipsReturnedRef.current = options.onChipsReturned;
  onSeatResolutionRef.current = options.onSeatResolution;
  onInsuranceAcceptedRef.current = options.onInsuranceAccepted;
  onInsuranceResolvedRef.current = options.onInsuranceResolved;
  onDoubleDownRef.current = options.onDoubleDown;
  onSplitRef.current = options.onSplit;
  onSurrenderRef.current = options.onSurrender;
  onDealerHitsRef.current = options.onDealerHits;
  onDealerRevealsHoleRef.current = options.onDealerRevealsHole;
  onDealerBlackjackRef.current = options.onDealerBlackjack;
  onDealerStandsRef.current = options.onDealerStands;
  onDealerBustsRef.current = options.onDealerBusts;
  onMidRoundBustRef.current = options.onMidRoundBust;
  onAllResolutionsCompleteRef.current = options.onAllResolutionsComplete;
  onPlayerSitOutRef.current = options.onPlayerSitOut;
  onDealingStartedRef.current = options.onDealingStarted;
  onPlayerTurnsStartedRef.current = options.onPlayerTurnsStarted;
  onInsuranceOfferedRef.current = options.onInsuranceOffered;
  onSeatRakeRef.current = options.onSeatRake;
  onDealerRakeRef.current = options.onDealerRake;
  onBetConfirmedRef.current = options.onBetConfirmed;
  onOpponentBetConfirmedRef.current = options.onOpponentBetConfirmed;
  onOpponentPayoutToKittyRef.current = options.onOpponentPayoutToKitty;
  onOpponentKittyToAvatarRef.current = options.onOpponentKittyToAvatar;
  onOpponentLossToDealerRef.current = options.onOpponentLossToDealer;
  onShowOutcomeLabelRef.current = options.onShowOutcomeLabel;
  onEnsurePixiSceneHealthRef.current = options.onEnsurePixiSceneHealth;
  onSyncDealtCountsRef.current = options.onSyncDealtCounts;
  onSidebetPlacedRef.current = options.onSidebetPlaced;
  onSidebetResolvedRef.current = options.onSidebetResolved;
  onSidebetChipsPaidRef.current = options.onSidebetChipsPaid;
  onSidebetChipsCollectedRef.current = options.onSidebetChipsCollected;
  onSidebetRemovedRef.current = options.onSidebetRemoved;
  onKeyedResyncAcceptedRef.current = options.onKeyedResyncAccepted;
  onOpponentHandStraightenedRef.current = options.onOpponentHandStraightened;
  // PIXI-ONLY opponent animation ref updates
  onOpponentSeatRakeRef.current = options.onOpponentSeatRake;
  onOpponentInsuranceAcceptedRef.current = options.onOpponentInsuranceAccepted;
  onOpponentInsuranceResolvedRef.current = options.onOpponentInsuranceResolved;
  onOpponentDoubleDownRef.current = options.onOpponentDoubleDown;
  onOpponentSplitRef.current = options.onOpponentSplit;
  onOpponentSurrenderRef.current = options.onOpponentSurrender;
  onInsuranceDecisionRef.current = options.onInsuranceDecision;
  onActiveHandChangeRef.current = options.onActiveHandChange;
  onScorecardUpdateRef.current = options.onScorecardUpdate;

  const enqueueBatch = useAnimationQueueStore((state) => state.enqueueBatch);
  const setRenderer = useAnimationQueueStore((state) => state.setRenderer);

  const processEvent = useGameStateStore((state) => state.processEvent);
  const setTable = useGameStateStore((state) => state.setTable);
  const setDealerPersona = useGameStateStore((state) => state.setDealerPersona);
  const setPenetration = useGameStateStore((state) => state.setPenetration);
  const setSeatedPlayers = useGameStateStore((state) => state.setSeatedPlayers);
  const syncFromTableState = useGameStateStore((state) => state.syncFromTableState);
  const setMySeat = useGameStateStore((state) => state.setMySeat);

  // Initialize renderer with callbacks
  // ===========================================
  // CRITICAL: NO-OP CALLBACKS IN RENDERER
  // ===========================================
  // Several callbacks are intentionally NO-OPs here because they are now
  // handled INLINE in handleGameEvents (not through the animation queue).
  //
  // WHY: The animation queue processes events asynchronously. When batches
  // arrive rapidly, the queue can process DEALER_REVEALS_HOLE from batch 2
  // before batch 1's CARD_DEALT animations complete. This caused the dealer's
  // hole card to stay face-down through entire rounds.
  //
  // FIX: These events are now awaited INLINE in handleGameEvents, guaranteeing
  // they execute in batch order. The renderer callbacks are NO-OPs to prevent
  // double-triggering.
  //
  // See: docs/BUG_REGISTRY.md - BUG-001: Persistent Hole Card
  // See: docs/BUG_REGISTRY.md - BUG-004: Animation Queue vs Inline Race
  // ===========================================
  useEffect(() => {
    const renderer = createWebAnimationRenderer({
      callbacks: {
        // Non-deferred callbacks - passed through directly
        onHandBusted: options.onHandBusted,
        onHandBlackjack: options.onHandBlackjack,
        // CRITICAL NO-OP: Dealer events handled inline - see docs/BUG_REGISTRY.md BUG-001
        onDealerRevealsHole: async () => {
          // No-op - triggered immediately in handleGameEvents
        },
        onDealerStands: async () => {
          // No-op - triggered immediately in handleGameEvents
        },
        onDealerBusts: async () => {
          // No-op - triggered immediately in handleGameEvents
        },
        // CRITICAL NO-OP: Chip events handled inline to ensure OOO
        onChipsPaid: async () => {
          // No-op - triggered immediately in handleGameEvents
        },
        onChipsCollected: async () => {
          // No-op - triggered immediately in handleGameEvents
        },

        // CRITICAL NO-OP: Card events handled inline - see docs/BUG_REGISTRY.md BUG-004
        onDealerHits: async () => {
          // No-op - triggered immediately in handleGameEvents
        },
        onCardDealt: async () => {
          // No-op - triggered immediately in handleGameEvents
        },

        // Deferred event callbacks - process state THEN call user callback
        // Animation callbacks - just pass through to user callbacks
        // State is updated immediately when events arrive (no deferral)
        onRoundCompleted: async () => {
          // No-op - state already updated
        },
        onRoundStarted: async (roundNumber, buttonPosition) => {
          // Diagnostic: Mark new round start
          diagLog.newRound(roundNumber);
          // Initialize orchestrator for new round
          getAnimationOrchestrator().startRound(roundNumber);
          // CRITICAL: Reset lastEventSequence for new round
          // Without this, events from the new round get skipped because
          // their sequence numbers (starting from 1) are <= the old lastEventSequence
          console.log(`[useGameEvents] New round ${roundNumber} - resetting lastEventSequence from ${useGameStateStore.getState().lastEventSequence} to 0`);
          useGameStateStore.setState({ lastEventSequence: 0 });
          await options.onRoundStarted?.(roundNumber, buttonPosition);
        },
        onScoreUpdated: async () => {
          // No-op - state already updated
        },
      },
    });

    rendererRef.current = renderer;
    setRenderer(renderer);

    return () => {
      rendererRef.current = null;
    };
  }, [setRenderer, processEvent, options.onHandBusted, options.onHandBlackjack, options.onRoundStarted]);

  // Set up round ACK callback for backpressure control
  // When client finishes processing ROUND_COMPLETED, it sends an ACK to the server
  // This prevents the server from overwhelming the client with events
  useEffect(() => {
    if (!socket || !tableId) return;

    const clientTableMaster = getClientTableMaster();
    clientTableMaster.setRoundAckHandler((round: number, batchSequence: number) => {
      socket.emit('client_message', {
        type: 'round_ack',
        tableId,
        round,
        batchSequence,
        timestamp: Date.now(),
      });
      console.log(`[useGameEvents] Sent round ACK: round=${round}, batch=${batchSequence}`);
    });

    return () => {
      // Clear callback on unmount
      clientTableMaster.setRoundAckHandler(() => {});
    };
  }, [socket, tableId]);

  // Handle incoming game events from socket
  // batchKey: Optional identifier for this batch (e.g., "11-3" for round 11, batch 3)
  // Used to detect when this batch has been superseded by a newer batch after timeout
  const handleGameEvents = useCallback(
    (message: { type: string; events?: BlackjackEvent[] }, batchKey?: string) => {
      if (message.type !== 'game_events' || !message.events) return;

      console.log('[useGameEvents] Processing', message.events.length, 'events:',
        message.events.map(e => e.type).join(', '), batchKey ? `(batch ${batchKey})` : '');

      // NOTE: Chip clone capture disabled - the old animation system (chipAnimations.ts)
      // creates its own flying chips and doesn't use unified clones from unifiedChipAnimations.ts.
      // Capturing clones here was creating orphaned DOM elements that persisted after animation.

      // Enqueue events for animation processing
      enqueueBatch(message.events);

      // Process events with async handling for animations that must complete before subsequent events.
      // Chained onto processingQueueRef to serialize batch processing — prevents race condition
      // where a new round's batch interleaves with the old round's pending animations.
      const events = message.events;

      // ===========================================
      // DYNAMIC BATCH TIMEOUT CALCULATION
      // ===========================================
      // Calculate expected batch duration based on event types, then add a buffer.
      // This prevents false-positive timeouts during legitimate long batches while
      // still catching genuinely stuck processing.
      //
      // Animation durations are now provided by the server in event.duration field.
      // This ensures server and client timing are always in sync.
      // The server calculates duration using runtime-configurable values.
      //
      const calculateBatchTimeout = (eventList: BlackjackEvent[]): number => {
        const BASE_TIMEOUT_MS = 3000; // Minimum 3s for processing overhead
        const BUFFER_MULTIPLIER = 1.2; // 20% buffer above expected duration
        const PER_EVENT_BUFFER = 50; // Small buffer per event for React rendering

        let expectedDurationMs = 0;

        for (const event of eventList) {
          // Use server-provided duration if available, otherwise fallback to small buffer
          const eventDuration = (event as any).duration ?? 0;
          expectedDurationMs += eventDuration + PER_EVENT_BUFFER;
        }

        // Apply buffer and enforce minimum
        const timeoutMs = Math.max(BASE_TIMEOUT_MS, expectedDurationMs * BUFFER_MULTIPLIER);

        // Cap at 30 seconds - if calculation exceeds this, something is wrong
        return Math.min(timeoutMs, 30000);
      };

      const BATCH_TIMEOUT_MS = calculateBatchTimeout(events);

      processingQueueRef.current = processingQueueRef.current.then(async () => {
        // ===========================================
        // ARCHITECTURE SIMPLIFICATION (2024-06)
        // ===========================================
        // ClientTableMaster is now the SINGLE GATEKEEPER for event sequencing.
        // All batch comparison, timeout, and safety net logic has been removed.
        // If ClientTableMaster releases an event, it MUST be processed.
        //
        // FLAGGED FOR DELETION: The commented code below was causing conflicts
        // where multiple checkpoints would fight each other (e.g., supersede
        // logic says "process critical event" but safety net says "skip it").
        // ===========================================

        const myBatchKey = batchKey || `unknown-${Date.now()}`;

        // ===========================================
        // FLAGGED FOR DELETION: OLD_BATCH_SKIPPED logic
        // This was causing dealing events to be skipped after split hands.
        // ClientTableMaster now handles all sequencing.
        // ===========================================
        /*
        // Only update activeBatchRef if this batch is newer than the current active batch.
        // This prevents older batches (that arrive late due to network delays) from
        // superseding newer batches that are already being processed.
        const shouldBecomeActive = (() => {
          const currentActive = activeBatchRef.current;
          if (!currentActive) return true; // No active batch, we should become active

          // Parse batch keys: format is "round-batchSequence"
          const currentParts = currentActive.split('-');
          const newParts = myBatchKey.split('-');

          // Handle non-standard keys (e.g., "unknown-timestamp")
          if (currentParts.length !== 2 || newParts.length !== 2) return true;

          const currentRound = parseInt(currentParts[0], 10);
          const currentSeq = parseInt(currentParts[1], 10);
          const newRound = parseInt(newParts[0], 10);
          const newSeq = parseInt(newParts[1], 10);

          // Handle parse failures
          if (isNaN(currentRound) || isNaN(newRound)) return true;

          // New batch is newer if from a later round, or same round with higher sequence
          return newRound > currentRound || (newRound === currentRound && newSeq > currentSeq);
        })();

        if (shouldBecomeActive) {
          // Check if this batch is from a new round - if so, reset lastEventSequence
          // This is CRITICAL: Without this reset, events from the new round get skipped
          // because their sequence numbers (starting from 1) are <= the old lastEventSequence
          const batchParts = myBatchKey.split('-');
          if (batchParts.length === 2) {
            const batchRound = parseInt(batchParts[0], 10);
            if (!isNaN(batchRound) && batchRound > lastActiveBatchRoundRef.current) {
              const oldSeq = useGameStateStore.getState().lastEventSequence;
              console.log(`[useGameEvents] New round ${batchRound} detected (was round ${lastActiveBatchRoundRef.current}) - resetting lastEventSequence from ${oldSeq} to 0`);
              useGameStateStore.setState({ lastEventSequence: 0 });
              lastActiveBatchRoundRef.current = batchRound;
            }
          }
          activeBatchRef.current = myBatchKey;
        } else {
          // This batch is older than the current active batch
          // BUT: Never skip batches containing dealing-phase events - cards MUST be dealt
          const CRITICAL_DEALING_EVENTS = ['CARD_DEALT', 'HAND_BOX_CREATED', 'DEALING_STARTED', 'INITIAL_DEAL_COMPLETE'];
          const hasCriticalDealingEvent = events.some(e => CRITICAL_DEALING_EVENTS.includes(e.type));

          if (hasCriticalDealingEvent) {
            console.log(`[useGameEvents] OLD BATCH ${myBatchKey} contains critical dealing events - processing anyway (active: ${activeBatchRef.current})`);
            // Don't update activeBatchRef - let the newer batch stay "active"
            // But DO process this batch's events
          } else {
            console.log(`[useGameEvents] SKIPPING OLD BATCH: ${myBatchKey} (active batch ${activeBatchRef.current} is newer)`);
            diagLog.recv({
              seq: events[0]?.sequenceNumber || 0,
              type: 'OLD_BATCH_SKIPPED',
              details: {
                skippedBatch: myBatchKey,
                activeBatch: activeBatchRef.current,
                eventCount: events.length,
                eventTypes: events.map(e => e.type).join(','),
              },
            });
            return; // Exit early, don't process this old batch
          }
        }
        */

        // Initialize batch tracking state for logging (timeout removed)
        batchProcessingStateRef.current = {
          eventCount: events.length,
          eventTypes: events.map(e => e.type),
          startTime: Date.now(),
          calculatedTimeout: 0, // No timeout - events process to completion
          lastProcessedEvent: null,
          lastProcessedIndex: -1,
        };

        // ===========================================
        // FLAGGED FOR DELETION: Batch timeout logic
        // Timeouts were causing batches to be abandoned mid-processing,
        // which led to skipped CARD_DEALT events after split hands.
        // If something hangs, it's a bug to fix, not a condition to work around.
        // ===========================================
        /*
        // Create a timeout promise that rejects after BATCH_TIMEOUT_MS
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error('BATCH_TIMEOUT'));
          }, BATCH_TIMEOUT_MS);
        });
        */

        // Wrap the actual batch processing
        const processBatch = async () => {
        console.log(`[useGameEvents DIAG] Processing batch of ${events.length} events START`);

        // Diagnostic: Log batch receipt
        const seqNumbers = events.map(e => e.sequenceNumber);
        diagLog.recvBatch({
          count: events.length,
          types: events.map(e => e.type),
          seqRange: [Math.min(...seqNumbers), Math.max(...seqNumbers)],
        });

        // ===========================================
        // SORT EVENTS BY SEQUENCE NUMBER
        // ===========================================
        // Events within a batch may arrive out of sequence order due to
        // async event generation on the server. Sort by sequenceNumber
        // to ensure proper processing order and prevent SKIPPED events.
        events.sort((a, b) => a.sequenceNumber - b.sequenceNumber);

        // PRE-SCAN: Record all SCORE_UPDATED events BEFORE processing animations
        // This ensures server-provided scores are available when card animations
        // complete and trigger scorebox re-renders. Without this, SCORE_UPDATED
        // would be blocked behind CARD_DEALT await, causing race conditions.
        //
        // Track card counts per hand as we scan through CARD_DEALT events,
        // so we can correctly associate each SCORE_UPDATED with its card count.
        const preScanCardCounts = new Map<string, number>(); // key: "seatPos-handIndex"
        for (const event of events) {
          // Clear score history FIRST when new round starts (before recording new scores)
          if (event.type === 'DEALING_STARTED') {
            clearAllScoreHistory();
            incrementRoundAndLogDiagnostics(); // Stress test diagnostics every 50 rounds
            console.log('[SCORE PRE-SCAN] DEALING_STARTED - cleared score history');
          }

          // Track CARD_DEALT to know how many cards have been dealt to each hand
          if (event.type === 'CARD_DEALT') {
            const ce = event as BlackjackEvent & { target: string; seatPosition?: number; handIndex: number };
            if (ce.target === 'player' && ce.seatPosition !== undefined) {
              const key = `${ce.seatPosition}-${ce.handIndex}`;
              const baseCount = useGameStateStore.getState().players.get(ce.seatPosition)?.hands[ce.handIndex]?.cards.length ?? 0;
              const seenCount = preScanCardCounts.get(key) ?? baseCount;
              preScanCardCounts.set(key, seenCount + 1);
            }
          }

          // Record SCORE_UPDATED with the correct card count
          if (event.type === 'SCORE_UPDATED') {
            const se = event as BlackjackEvent & {
              target: 'player' | 'dealer';
              seatPosition?: number;
              handIndex: number;
              score: number;
              isSoft: boolean;
            };

            if (se.target === 'player' && se.seatPosition !== undefined) {
              const key = `${se.seatPosition}-${se.handIndex}`;
              // Use pre-scan count (includes cards not yet processed) or fall back to store
              const cardCount = preScanCardCounts.get(key) ??
                (useGameStateStore.getState().players.get(se.seatPosition)?.hands[se.handIndex]?.cards.length ?? 0);

              recordScoreSnapshot(se.seatPosition, se.handIndex, cardCount, se.score, se.isSoft);
              console.log(`[SCORE PRE-SCAN] seat=${se.seatPosition} hand=${se.handIndex} cardCount=${cardCount} score=${se.score} isSoft=${se.isSoft}`);
            }
          }
        }

        // Track if dealer reveal was already animated (during INSURANCE_RESOLVED)
        let dealerRevealAlreadyAnimated = false;

        for (let i = 0; i < events.length; i++) {
          const event = events[i];
          try {
            // ===========================================
            // FLAGGED FOR DELETION: BATCH_SUPERSEDED logic
            // This was checking if a newer batch had taken over and aborting.
            // With timeout logic removed, batches can't be superseded.
            // ClientTableMaster handles all sequencing - we just process events.
            // ===========================================
            /*
            // Check if this batch has been superseded by a newer batch.
            // This happens when a timeout occurs and the queue is reset - a new batch
            // starts processing while this batch is still running. The old batch should
            // abort to prevent out-of-order event processing.
            // EXCEPTION: Critical events must always be processed even if batch is superseded:
            // - DEALER_RAKE: Avoid orphaned dealer cards after split hands
            // - ROUND_COMPLETED: Release queued next-round events
            // - CARD_DEALT, HAND_BOX_CREATED, DEALING_STARTED, INITIAL_DEAL_COMPLETE: Cards MUST be dealt
            // After split hands, SEAT_RAKE takes longer than server expects, so batches get superseded.
            if (activeBatchRef.current !== myBatchKey) {
              // Check if critical events are pending in remaining events
              const remainingEvents = events.slice(i);
              const CRITICAL_EVENT_TYPES = ['DEALER_RAKE', 'ROUND_COMPLETED', 'CARD_DEALT', 'HAND_BOX_CREATED', 'DEALING_STARTED', 'INITIAL_DEAL_COMPLETE'];
              const hasCriticalEvent = remainingEvents.some(e => CRITICAL_EVENT_TYPES.includes(e.type));
              const isCriticalEvent = CRITICAL_EVENT_TYPES.includes(event.type);

              if (hasCriticalEvent && !isCriticalEvent) {
                // Skip non-critical events but continue to critical events
                console.log(`[useGameEvents] SUPERSEDED: Skipping ${event.type} to process pending critical event`);
                continue;
              }

              if (isCriticalEvent) {
                // CRITICAL: Process DEALER_RAKE/ROUND_COMPLETED even though batch is superseded
                console.log(`[useGameEvents] SUPERSEDED but processing CRITICAL event: ${event.type}`);
                // Fall through to process the critical event
              } else {
                // No critical events pending - safe to abort
                console.log(`[useGameEvents] ABORTING SUPERSEDED BATCH: ${myBatchKey} (active batch is now ${activeBatchRef.current})`);
                diagLog.recv({
                  seq: event.sequenceNumber,
                  type: 'BATCH_SUPERSEDED',
                  details: {
                    abortedBatch: myBatchKey,
                    activeBatch: activeBatchRef.current,
                    remainingEvents: events.length - i,
                    eventTypes: remainingEvents.map(e => e.type).join(','),
                  },
                });
                break; // Exit the event loop, don't process remaining events
              }
            }
            */

            const preState = useGameStateStore.getState();
            const tabHidden = document.visibilityState === 'hidden';
            console.log(`[useGameEvents DIAG] Event ${i+1}/${events.length}: ${event.type} seq=${event.sequenceNumber} (lastSeq=${preState.lastEventSequence}, turnSignal=${preState.turnSignal}, tabHidden=${tabHidden}, availableActions=${JSON.stringify(preState.availableActions)})`);

            // DEBUG: Special trace for DEALER_RAKE
            if (event.type === 'DEALER_RAKE') {
              console.log(`[DEALER_RAKE_TRACE] Received DEALER_RAKE event seq=${event.sequenceNumber}, lastSeq=${preState.lastEventSequence}`);
            }

            // DIAGNOSTIC: Trace any RESOLUTION or SPLIT events to debug event handling
            if (event.type.includes('RESOLUTION') || event.type.includes('SPLIT') || event.type.includes('RESOLVED')) {
              console.log(`[DIAG TRACE] Event type="${event.type}" seq=${event.sequenceNumber} data=${JSON.stringify(event).slice(0, 200)}`);
            }

            // ===========================================
            // FLAGGED FOR DELETION: SAFETY NET sequence check
            // This was blocking events that had "already been processed" based on
            // sequence numbers, but it was causing conflicts with the supersede logic.
            // ClientTableMaster now handles all duplicate detection.
            // ===========================================
            /*
            // SAFETY NET for reconnection/network edge cases.
            // ServerTableMaster guarantees event ordering - this catches:
            // - Duplicate events from reconnection state sync
            // - Events that arrive after their batch already processed
            //
            // If this fires during normal gameplay, it may indicate server ordering issue.
            // Handle race condition where round N+1's events arrive while round N's batch is still finishing.
            // FIX: Compare round NUMBER (not full roundId) - roundId format is "tableId-roundNumber-timestamp"
            // The timestamp portion differs even within the same round, so we extract the round number.
            const currentRoundNumber = preState.round?.roundNumber;
            const eventRoundId = event.roundId;
            // Extract round number from roundId (format: "tableId-roundNumber-timestamp")
            const eventRoundNumberMatch = eventRoundId?.match(/-(\d+)-\d+$/);
            const eventRoundNumber = eventRoundNumberMatch ? parseInt(eventRoundNumberMatch[1], 10) : null;
            // If we have an event round but NO current round state (cleared between rounds), treat as new round
            const isNewRoundAfterCleared = currentRoundNumber == null && eventRoundNumber != null;
            const isDifferentRound = isNewRoundAfterCleared ||
              (currentRoundNumber != null && eventRoundNumber != null && eventRoundNumber !== currentRoundNumber);
            // CRITICAL EVENTS: ROUND_COMPLETED must ALWAYS be processed to release queued next-round events.
            // Even if sequence appears "already processed" (due to batch supersession), ROUND_COMPLETED
            // triggers roundCompleted() which is idempotent and required for round transitions.
            const isCriticalEventType = event.type === 'ROUND_COMPLETED';
            const isDuplicateInSameRound = !isDifferentRound && !isCriticalEventType && event.sequenceNumber <= preState.lastEventSequence;
            if (isDifferentRound) {
              const reason = isNewRoundAfterCleared
                ? `round state cleared, event has round=${eventRoundNumber}`
                : `event round=${eventRoundNumber} != current round=${currentRoundNumber}`;
              console.log(`[useGameEvents] NEW ROUND detected: ${reason}, processing ${event.type} seq=${event.sequenceNumber}`);
            }
            if (isCriticalEventType && event.sequenceNumber <= preState.lastEventSequence) {
              console.log(`[useGameEvents] CRITICAL EVENT bypassing safety net: ${event.type} seq=${event.sequenceNumber} (lastSeq=${preState.lastEventSequence})`);
            }
            if (isDuplicateInSameRound) {
              console.log(`[useGameEvents] SAFETY NET: Skipping already-processed event: ${event.type} seq=${event.sequenceNumber} (lastSeq=${preState.lastEventSequence}, roundId=${eventRoundId?.slice(-8)})`);
              // Diagnostic: Log skipped event
              diagLog.recvSkip({ seq: event.sequenceNumber, type: event.type, reason: `seq <= lastSeq (${preState.lastEventSequence})` });
              // DEBUG: Extra warning for DEALER_RAKE
              if (event.type === 'DEALER_RAKE') {
                console.warn(`[DEALER_RAKE_TRACE] ⚠️ DEALER_RAKE SKIPPED by safety net! seq=${event.sequenceNumber}, lastSeq=${preState.lastEventSequence}`);
              }
              continue;
            }
            */

            // Diagnostic: Log event received (extract seat/hand/cardIndex if present)
            const eventAny = event as BlackjackEvent & { seatPosition?: number; handIndex?: number; cardIndex?: number; target?: string };
            diagLog.recv({
              seq: event.sequenceNumber,
              type: event.type,
              seat: eventAny.seatPosition,
              hand: eventAny.handIndex,
              details: event.type === 'CARD_DEALT' ? { cardIndex: eventAny.cardIndex, target: eventAny.target } : undefined,
            });

            // Update batch tracking state for timeout logging
            if (batchProcessingStateRef.current) {
              batchProcessingStateRef.current.lastProcessedEvent = event.type;
              batchProcessingStateRef.current.lastProcessedIndex = i;
            }

            // SPECIAL CASE: For INSURANCE_RESOLVED, we need to:
            // 1. First trigger DEALER_REVEALS_HOLE animation if it's in this batch (dealer shows 21 first)
            // 2. Capture insurance bets BEFORE processing (because processEvent clears state)
            // 3. Trigger animation callback BEFORE processing (because processEvent removes DOM element)
            // 4. Then process the event
            if (event.type === 'INSURANCE_RESOLVED') {
              // Clear insurance recommendation since decision has been made
              useGameStateStore.getState().clearServerInsuranceRecommendation();
              console.log(`[Recommendation] Cleared insurance recommendation on INSURANCE_RESOLVED`);

              try {
                // Check if DEALER_REVEALS_HOLE is in this batch - if so, reveal dealer first
                const dealerRevealEvent = events.find(e => e.type === 'DEALER_REVEALS_HOLE');
                if (dealerRevealEvent) {
                  console.log('[TIMING] INSURANCE_RESOLVED: Revealing dealer hole card FIRST');
                  const re = dealerRevealEvent as BlackjackEvent & { holeCard: Card; flipDuration?: number };
                  useCardTrackingStore.getState().trackCard(re.holeCard);
                  await onDealerRevealsHoleRef.current?.(re.flipDuration);
                  dealerRevealAlreadyAnimated = true;
                  // Small delay to let dealer reveal animation complete
                  await new Promise(resolve => setTimeout(resolve, 400));
                }
                const gameState = useGameStateStore.getState();
                const mySeat = gameState.mySeatPosition;
                const insuranceBetsByPlayer = new Map<number, number>();
                gameState.players.forEach((player, seatPosition) => {
                  if (player.insuranceBet) {
                    insuranceBetsByPlayer.set(seatPosition, player.insuranceBet);
                  }
                });

                const resolvedEvent = event as BlackjackEvent & {
                  dealerHasBlackjack: boolean;
                  payouts: Array<{ seatPosition: number; amount: number }>;
                };
                console.log(`[TIMING] INSURANCE_RESOLVED animation triggered BEFORE state processing at ${Date.now()}`, resolvedEvent);

                // If dealer has blackjack, straighten dealer hand (no DEALER_STANDS event will fire)
                // Orchestrator handles stale animation cleanup - no timeout needed
                if (resolvedEvent.dealerHasBlackjack) {
                  console.log('[TIMING] Dealer has blackjack - calling onDealerBlackjack to straighten hand');
                  await getAnimationOrchestrator().animate(
                    'insurance-dealer-blackjack',
                    'DEALER_FINISH',
                    onDealerBlackjackRef.current?.() ?? Promise.resolve()
                  );
                }

                const payoutsBySeat = new Map<number, number>();
                if (resolvedEvent.payouts) {
                  for (const payout of resolvedEvent.payouts) {
                    payoutsBySeat.set(payout.seatPosition, payout.amount);
                  }
                }

                // Await each resolution animation so DOM elements remain until
                // animations complete (processEvent clears state after this block).
                for (const [seatPosition, insuranceBet] of insuranceBetsByPlayer) {
                  if (insuranceBet > 0) {
                    const payoutAmount = payoutsBySeat.get(seatPosition) || 0;
                    const isOpponent = seatPosition !== mySeat;

                    const orchestrator = getAnimationOrchestrator();
                    const animationId = `insurance-resolve-seat-${seatPosition}`;

                    if (isOpponent) {
                      // PIXI-ONLY: Opponent insurance resolution via Pixi callback
                      console.log(`[TIMING] INSURANCE_RESOLVED (opponent seat ${seatPosition}): dealerHasBlackjack=${resolvedEvent.dealerHasBlackjack}, payout=${payoutAmount}, insuranceBet=${insuranceBet}`);

                      const insuranceChips = breakdownChips(insuranceBet);
                      const payoutChips = payoutAmount > 0 ? breakdownChips(payoutAmount) : [];

                      if (onOpponentInsuranceResolvedRef.current) {
                        await orchestrator.animate(animationId, 'INSURANCE_RESOLVE',
                          onOpponentInsuranceResolvedRef.current(seatPosition, resolvedEvent.dealerHasBlackjack, insuranceChips, payoutChips)
                        );
                      } else {
                        console.warn(`[useGameEvents] No onOpponentInsuranceResolved callback for seat ${seatPosition}`);
                      }
                      // Clear insurance bet from store
                      useGameStateStore.getState().clearInsuranceBet(seatPosition);
                      console.log(`[TIMING] INSURANCE_RESOLVED (opponent seat ${seatPosition}) - cleared insurance bet`);
                    } else {
                      // Current user: use the callback for full animation control
                      console.log(`[TIMING] Calling onInsuranceResolved for seat ${seatPosition}: dealerHasBlackjack=${resolvedEvent.dealerHasBlackjack}, payout=${payoutAmount}, insuranceBet=${insuranceBet}`);

                      // Balance tracking: only track when user wins insurance (payout > 0)
                      // The insurance bet deduction was tracked when INSURANCE_DECISION was processed
                      if (resolvedEvent.dealerHasBlackjack && payoutAmount > 0) {
                        const clientTableMaster = getClientTableMaster();
                        clientTableMaster.registerBalanceAnimation('INSURANCE_RESOLVED', payoutAmount, seatPosition);
                      }

                      await orchestrator.animate(
                        animationId,
                        'INSURANCE_RESOLVE',
                        onInsuranceResolvedRef.current?.(
                          seatPosition,
                          resolvedEvent.dealerHasBlackjack,
                          payoutAmount,
                          insuranceBet
                        ) ?? Promise.resolve()
                      );

                      // Balance tracking: animation complete
                      if (resolvedEvent.dealerHasBlackjack && payoutAmount > 0) {
                        const clientTableMaster = getClientTableMaster();
                        clientTableMaster.balanceAnimationComplete();
                      }
                    }
                  }
                }
              } catch (insErr) {
                console.error('[useGameEvents] INSURANCE_RESOLVED pre-processing error (non-fatal):', insErr);
              }
            }

            // SPECIAL CASE: For PREMATURE_RESOLUTION bust during splits, capture bet BEFORE processEvent
            let midRoundBustInfo: { seatPosition: number; handIndex: number; betAmount: number; betChips?: number[] } | null = null;
            if (event.type === 'PREMATURE_RESOLUTION') {
              const premEvent = event as BlackjackEvent & { seatPosition: number; handIndex: number; reason: string; outcome: string };
              if (premEvent.reason === 'bust') {
                const gameState = useGameStateStore.getState();
                const player = gameState.players.get(premEvent.seatPosition);
                if (player && player.hands.length > 1) {
                  const hand = player.hands[premEvent.handIndex];
                  if (hand) {
                    midRoundBustInfo = {
                      seatPosition: premEvent.seatPosition,
                      handIndex: premEvent.handIndex,
                      betAmount: hand.bet,
                      betChips: hand.betChips,
                    };
                  }
                }
              }
            }

            // HAND_BUSTED: Capture bet info BEFORE processEvent so we can animate chips immediately
            // This enables immediate chip resolution even as gameplay continues with next player
            let handBustedInfo: { seatPosition: number; handIndex: number; betAmount: number; betChips?: number[] } | null = null;
            if (event.type === 'HAND_BUSTED') {
              const bustEvent = event as BlackjackEvent & { seatPosition: number; handIndex: number };
              const gameState = useGameStateStore.getState();
              const player = gameState.players.get(bustEvent.seatPosition);
              if (player) {
                const hand = player.hands[bustEvent.handIndex];
                if (hand) {
                  handBustedInfo = {
                    seatPosition: bustEvent.seatPosition,
                    handIndex: bustEvent.handIndex,
                    betAmount: hand.bet,
                    betChips: hand.betChips,
                  };
                }
              }
            }

            // ALL_RESOLUTIONS_COMPLETE signals that all chip events have been sent
            // This is the trigger to process ALL queued chip events in correct order
            if (event.type === 'ALL_RESOLUTIONS_COMPLETE') {
              getAnimationOrchestrator().setPhase('RESOLVING');
              console.log('[ALL_RESOLUTIONS_COMPLETE] Triggering chip resolution for all queued events');
              await onAllResolutionsCompleteRef.current?.();
            }

            // DEFERRED PLAYER CARD RAKE:
            // Capture visible clones at ROUND_COMPLETED (cards still in React DOM).
            // Clones are immediately visible — they overlay the real cards with
            // identical appearance (cloneNode + z-index 9999). When ROUND_STARTED
            // clears React state, clones persist seamlessly. Players' clones stay
            // until each player presses BET (see TablePage handlePlaceBet).
            // Dealer clones are also captured and raked after player cards.
            if (event.type === 'ROUND_COMPLETED') {
              // Signal end of round to orchestrator (clears all active animations)
              getAnimationOrchestrator().endRound();

              // Flush any pending balance updates at round end
              // This ensures balance is accurate even if animations were skipped
              getClientTableMaster().flushBalance();

              // Clear all server recommendations at round end
              const gameState = useGameStateStore.getState();
              gameState.clearServerRecommendation();
              gameState.clearServerInsuranceRecommendation();
              console.log(`[Recommendation] Cleared all server recommendations on ROUND_COMPLETED`);

              // Reset sidebet counter for next round
              if (sidebetsPlacedThisRoundRef.current > 0) {
                console.log(`[ROUND_COMPLETED] Resetting sidebet counter (was ${sidebetsPlacedThisRoundRef.current})`);
                sidebetsPlacedThisRoundRef.current = 0;
              }

              // Retire live-removed sidebets at round boundary
              // These sidebets were removed mid-hand but their icons stayed for the current hand.
              // Now that the round is over, destroy their icons via the normal removal path.
              if (sidebetsPendingRetirementRef.current.size > 0) {
                const mySeatPos = gameState.mySeatPosition;
                if (mySeatPos != null) {
                  for (const sidebetType of sidebetsPendingRetirementRef.current) {
                    console.log(`[ROUND_COMPLETED] Retiring live-removed sidebet icon: ${sidebetType}`);
                    onSidebetRemovedRef.current?.(mySeatPos, sidebetType);
                  }
                }
                sidebetsPendingRetirementRef.current.clear();
              }

              // LOCK CAROUSEL IMMEDIATELY for split hands - BEFORE any async operations
              // This prevents the carousel from syncing to server state updates that
              // reset currentHandIndex to 0 during chip resolution
              const gameStateForCapture = useGameStateStore.getState();
              const myPos = gameStateForCapture.mySeatPosition;

              if (myPos != null) {
                const myPlayer = gameStateForCapture.players.get(myPos);
                if (myPlayer && myPlayer.hands.length > 1) {
                  // Lock to the last hand - in blackjack, hands are ALWAYS played in order
                  // (0→1→2→3), so the last hand is always the visible one at round end
                  const visibleHand = myPlayer.hands.length - 1;
                  console.log(`[ROUND_COMPLETED] Locking carousel to hand ${visibleHand} (last of ${myPlayer.hands.length})`);
                  useAnimationQueueStore.getState().setActiveHandOverride(visibleHand);
                }
              }

              // PIXI PURITY: DOM clone capture removed - Pixi manages all card visuals natively
              // captureAllPlayerCards();
              // captureDealerCards();
              // captureSingleSplitHandClones();
              // captureOpponentSplitHandCards();
              // ensureAllClonesExist();
              // All card capture/cloning is now handled by Pixi's internal card sprite management

              // PixiJS scene health-check: verify sprites and textures are intact
              // This catches scene corruption from memory leaks or extended play
              const healthReport = onEnsurePixiSceneHealthRef.current?.();
              if (healthReport && healthReport.issues.length > 0) {
                console.warn('[useGameEvents] PixiJS scene health issues detected:', healthReport.issues);
              }

              // NOTE: Client-side scorecard calculation removed.
              // Scorecard is now server-driven via SCORECARD_UPDATE event.

              // ===========================================
              // MARKED FOR DELETION: Client-side ACK delay
              // ===========================================
              // The server now paces the game with real timing (async delays in TableManager).
              // Events arrive at the pace they should be animated, so client doesn't need
              // to delay the ACK anymore. The server has already waited.
              //
              // OLD CODE (marked for deletion):
              // const roundCompletedEvent = event as import('@busted-blackjack/shared').RoundCompletedEvent;
              // const ackDelay = roundCompletedEvent.nextRoundDelay ?? 2000;
              // console.log(`[ROUND_COMPLETED] Scheduling round ACK in ${ackDelay}ms (nextRoundDelay)`);
              // setTimeout(() => {
              //   getClientTableMaster().sendPendingRoundAck();
              // }, ackDelay);
              //
              // NEW CODE: Send ACK immediately since server already paced the game
              console.log(`[ROUND_COMPLETED] Sending round ACK immediately (server-paced)`);
              getClientTableMaster().sendPendingRoundAck();

              // Signal round completion to ClientTableMaster to release any queued next-round events
              const completedRoundNumber = useGameStateStore.getState().round?.roundNumber;
              if (completedRoundNumber) {
                console.log(`[ROUND_COMPLETED] Signaling round ${completedRoundNumber} completed to ClientTableMaster`);
                getClientTableMaster().roundCompleted(completedRoundNumber);
              }
            }

            // Dealer cards are now captured at ROUND_COMPLETED and raked
            // after player cards when BET is pressed (see TablePage handleRakeCards)

            // PRE-HIDE: Inject CSS rule BEFORE processEvent so React renders
            // the new card already hidden — prevents the visible flash before
            // the deal animation callback fires and hides it manually.
            // The animation callback (onCardDealt / onDealerHits) removes the
            // rule when revealing the card after the flying animation lands.
            if (event.type === 'CARD_DEALT') {
              const ce = event as BlackjackEvent & { target: string; seatPosition?: number; handIndex: number; cardIndex: number };
              const seatId = ce.target === 'dealer' ? 'dealer' : String(ce.seatPosition);
              const hideRule = document.createElement('style');
              hideRule.setAttribute('data-prehide-card', `${seatId}-${ce.handIndex}-${ce.cardIndex}`);
              hideRule.textContent = `[data-animation-card][data-card-seat="${seatId}"][data-card-hand="${ce.handIndex}"][data-card-index="${ce.cardIndex}"] { visibility: hidden !important; }`;
              document.head.appendChild(hideRule);
            }
            if (event.type === 'DEALER_HITS') {
              const dealer = useGameStateStore.getState().dealer;
              const nextIdx = dealer.cards.length; // index the new card will have after processEvent
              const hideRule = document.createElement('style');
              hideRule.setAttribute('data-prehide-card', `dealer-0-${nextIdx}`);
              hideRule.textContent = `[data-animation-card][data-card-seat="dealer"][data-card-hand="0"][data-card-index="${nextIdx}"] { visibility: hidden !important; }`;
              document.head.appendChild(hideRule);
            }

            // PRE-HIDE OPPONENT BET CHIPS: Use React state to hide chips during animation
            // This is more reliable than DOM manipulation which can race with React's render cycle
            if (event.type === 'BET_CONFIRMED') {
              const betEvent = event as BlackjackEvent & { seatPosition: number; amount: number };
              const gameState = useGameStateStore.getState();
              const isOpponent = gameState.mySeatPosition !== betEvent.seatPosition;
              if (isOpponent && betEvent.amount > 0) {
                // Mark this seat as having a bet animation in progress
                // SplitChipStacks will hide chips when this is true
                useGameStateStore.getState().startOpponentBetAnimation(betEvent.seatPosition);
              }
            }

            // PIXI PURITY: DOM-based prehide rules and animateSplitCardSlide removed
            // Pixi handles all card slide animations and chip visibility directly via sprite management
            // Split card slide animation: Pixi's onSplit callback handles this natively
            // Opponent chip visibility: Pixi sprites are created invisible and animated to visible

            // PLAYER SIT-OUT: When a player times out, rake their cards immediately
            // This happens BEFORE processEvent so the animation uses current DOM state
            if (event.type === 'PLAYER_SIT_OUT') {
              const sitOutEvent = event as BlackjackEvent & { seatPosition: number };
              console.log(`[useGameEvents] PLAYER_SIT_OUT seat ${sitOutEvent.seatPosition} - raking their cards`);
              await onPlayerSitOutRef.current?.(sitOutEvent.seatPosition);
            }

            // FORCE-RAKE BEFORE DEALING: When DEALING_STARTED fires, force-rake all
            // remaining cards from the previous round before new cards are dealt.
            // This is a safety net - most cards should already be raked by BET or SIT_OUT.
            // Dealer is raked LAST so all players can compare their hands.
            if (event.type === 'DEALING_STARTED') {
              // Wait for ALL pending bet animations via orchestrator
              // This ensures order of operations: all bets land → then cards deal
              const orchestrator = getAnimationOrchestrator();

              // Wait for bet chip animations via orchestrator (with timeout for resilience)
              await orchestrator.waitForTypeWithTimeout('BET_CHIPS', 2000);

              // Wait for sidebet animations via orchestrator (only if sidebets were placed)
              const sidebetsThisRound = sidebetsPlacedThisRoundRef.current;
              sidebetsPlacedThisRoundRef.current = 0; // Reset for next round

              if (sidebetsThisRound > 0) {
                await orchestrator.waitForTypeWithTimeout('SIDEBET_PLACED', 2000);
              }

              // Reset sidebet resolution visual states (win/lose borders → gold)
              // This clears green/red borders from the previous round
              useGameStateStore.getState().resetAllPlayerSidebetResolutions();

              console.log('[useGameEvents] DEALING_STARTED - calling onDealingStarted for force-rake');
              // Note: Score history is cleared in pre-scan phase (before animations)
              await onDealingStartedRef.current?.();
            }

            // INSURANCE_OFFERED: Straighten dealer hand before insurance panel shows
            // This comes before PLAYER_TURNS_STARTED when dealer shows an Ace
            if (event.type === 'INSURANCE_OFFERED') {
              console.log('[useGameEvents] INSURANCE_OFFERED - straightening dealer hand before insurance panel');
              await onInsuranceOfferedRef.current?.();
            }

            // PLAYER_TURNS_STARTED: Straighten dealer hand before turn beam activates
            // This happens after the initial deal completes but before player actions begin
            if (event.type === 'PLAYER_TURNS_STARTED') {
              getAnimationOrchestrator().setPhase('PLAYER_TURNS');
              console.log('[useGameEvents] PLAYER_TURNS_STARTED - straightening dealer hand before turn beam');
              await onPlayerTurnsStartedRef.current?.();
            }

            // Handle SCORECARD_UPDATE - server-driven scorecard only
            // NOTE: Outcome labels are now shown ONLY during SEAT_RESOLUTION carousel rotation,
            // synchronized with hand labels. SCORECARD_UPDATE should NOT navigate or show labels.
            if (event.type === 'SCORECARD_UPDATE') {
              // ===========================================
              // SCORECARD TIMING: Step 4 - Client receives SCORECARD_UPDATE
              // ===========================================
              const step4Time = Date.now();
              console.log(`[SCORECARD TIMING] STEP 4: Client received SCORECARD_UPDATE at ${step4Time} (${new Date(step4Time).toISOString()})`);

              const gameState = useGameStateStore.getState();
              const scorecardEvent = event as BlackjackEvent & {
                seatPosition: number;
                hands: ScorecardHandResult[];
                sidebets: ScorecardSidebetResult[];
                insurance?: ScorecardInsuranceResult;
                totalNetResult: number;
                newBalance: number;
              };

              const isPlayer = scorecardEvent.seatPosition === gameState.mySeatPosition;

              // For player's seat only: update scorecard content and flip
              // Outcome labels are shown during SEAT_RESOLUTION, synchronized with carousel
              if (isPlayer) {
                console.log(`[SCORECARD_UPDATE] Processing scorecard for player seat ${scorecardEvent.seatPosition}: netResult=${scorecardEvent.totalNetResult}`);
                try {
                  // ===========================================
                  // SCORECARD TIMING: Step 5 - Calling onScorecardUpdate (triggers flip)
                  // ===========================================
                  const step5StartTime = Date.now();
                  console.log(`[SCORECARD TIMING] STEP 5: Starting onScorecardUpdate callback at ${step5StartTime}`);

                  await onScorecardUpdateRef.current?.(
                    scorecardEvent.hands,
                    scorecardEvent.sidebets,
                    scorecardEvent.insurance,
                    scorecardEvent.totalNetResult,
                    scorecardEvent.newBalance
                  );

                  const step5EndTime = Date.now();
                  console.log(`[SCORECARD TIMING] STEP 5: onScorecardUpdate complete at ${step5EndTime}, duration=${step5EndTime - step5StartTime}ms`);
                  console.log(`[SCORECARD TIMING] CLIENT TOTAL: Step 4→5 = ${step5EndTime - step4Time}ms`);
                } catch (err) {
                  console.error('[SCORECARD_UPDATE] Scorecard callback error:', err);
                }
              }
            }

            processEvent(event);

            // ===========================================
            // EMBEDDED RECOMMENDATION HANDLING
            // ===========================================
            // With the new architecture, recommendations are embedded directly in
            // TURN_STARTED and INSURANCE_OFFERED events instead of separate events.
            // This block extracts and processes those embedded recommendations.

            // Handle TURN_STARTED with embedded recommendation
            if (event.type === 'TURN_STARTED') {
              const turnEvent = event as BlackjackEvent & {
                seatPosition: number;
                handIndex: number;
                availableActions: PlayerAction[];
                recommendation?: {
                  action: 'HIT' | 'STAND' | 'DOUBLE' | 'SPLIT' | 'SURRENDER';
                  reason: string;
                  isDeviation?: boolean;
                  autoplayDelay?: number;
                };
              };

              // Carousel navigation: sync to the hand that has the turn
              const gameState = useGameStateStore.getState();
              const isMyTurn = gameState.mySeatPosition === turnEvent.seatPosition;

              if (isMyTurn) {
                const carousel = getSeatCarouselStore();
                const nav = carousel.getSeatNavigation(turnEvent.seatPosition);

                // If split hands exist and we're not viewing the active hand, navigate to it
                if (nav.totalHands > 1 && nav.viewedHandIndex !== turnEvent.handIndex) {
                  console.log(`[TURN_STARTED] Carousel not on active hand ${turnEvent.handIndex} (viewing ${nav.viewedHandIndex}), navigating...`);
                  carousel.navigateToHand(turnEvent.seatPosition, turnEvent.handIndex);
                }

                // Show active hand indicator (dims non-active hands)
                if (nav.totalHands > 1) {
                  onActiveHandChangeRef.current?.(turnEvent.seatPosition, turnEvent.handIndex);
                }
              }

              if (turnEvent.recommendation) {
                const rec = turnEvent.recommendation;
                const gameState = useGameStateStore.getState();
                const isMyEvent = gameState.mySeatPosition === turnEvent.seatPosition;

                console.log(`[Recommendation] TURN_STARTED embedded - seat ${turnEvent.seatPosition}, action: ${rec.action}, reason: ${rec.reason}${rec.isDeviation ? ' (DEVIATION)' : ''}, isMyEvent: ${isMyEvent}`);

                if (isMyEvent) {
                  // Store recommendation for BUDDY! to consume
                  useGameStateStore.getState().setServerRecommendation(
                    rec.action,
                    rec.reason,
                    rec.isDeviation ?? false,
                    turnEvent.seatPosition,
                    turnEvent.handIndex
                  );

                  // Check if autoplay is enabled
                  const user = useAuthStore.getState().user;
                  const isAutoplayEnabled = user?.autoPlay === true;

                  if (isAutoplayEnabled) {
                    const autoplayDelay = rec.autoplayDelay ?? 800;
                    console.log(`[Recommendation] Autoplay is ON - will execute ${rec.action} after ${autoplayDelay}ms delay`);

                    setTimeout(() => {
                      // Re-verify autoplay is still enabled and it's still our turn
                      const currentUser = useAuthStore.getState().user;
                      const currentGameState = useGameStateStore.getState();
                      if (currentUser?.autoPlay !== true) {
                        console.log(`[Recommendation] Autoplay was disabled during delay - skipping auto-execute`);
                        return;
                      }
                      if (currentGameState.currentTurnSeat !== turnEvent.seatPosition) {
                        console.log(`[Recommendation] Turn changed during delay - skipping auto-execute`);
                        return;
                      }

                      // Log for diagnostics
                      diagLog.action({
                        seat: turnEvent.seatPosition,
                        action: rec.action,
                        source: 'AUTOPLAY',
                      });

                      // Map recommended action to socket message type
                      const actionMap: Record<string, string> = {
                        'HIT': 'hit',
                        'STAND': 'stand',
                        'DOUBLE': 'double',
                        'SPLIT': 'split',
                        'SURRENDER': 'surrender',
                      };

                      const messageType = actionMap[rec.action];
                      if (messageType) {
                        const currentSocket = useSocketStore.getState().socket;
                        if (currentSocket) {
                          currentSocket.emit('client_message', { type: messageType });
                          console.log(`[Recommendation] Sent ${messageType} to server`);
                        }
                      }
                    }, autoplayDelay);
                  } else {
                    console.log(`[Recommendation] Autoplay is OFF - BUDDY! can consume recommendation`);
                  }
                }
              }
            }

            // Handle INSURANCE_OFFERED with embedded recommendations
            if (event.type === 'INSURANCE_OFFERED') {
              const insEvent = event as BlackjackEvent & {
                eligibleSeats: number[];
                recommendations?: Array<{
                  seatPosition: number;
                  action: 'INSURANCE' | 'NO_INSURANCE';
                  reason: string;
                  isDeviation?: boolean;
                  autoplayDelay?: number;
                }>;
              };

              if (insEvent.recommendations && insEvent.recommendations.length > 0) {
                const gameState = useGameStateStore.getState();
                const mySeatPosition = gameState.mySeatPosition;

                // Find recommendation for current user
                const myRec = insEvent.recommendations.find(r => r.seatPosition === mySeatPosition);

                if (myRec) {
                  console.log(`[Recommendation] INSURANCE_OFFERED embedded - seat ${myRec.seatPosition}, action: ${myRec.action}, reason: ${myRec.reason}${myRec.isDeviation ? ' (DEVIATION)' : ''}`);

                  // Store recommendation for BUDDY! to consume
                  useGameStateStore.getState().setServerInsuranceRecommendation(
                    myRec.action,
                    myRec.reason,
                    myRec.isDeviation ?? false,
                    myRec.seatPosition
                  );

                  // Check if autoplay is enabled
                  const user = useAuthStore.getState().user;
                  const isAutoplayEnabled = user?.autoPlay === true;

                  if (isAutoplayEnabled) {
                    // Use server-provided delay or random 1000-3000ms
                    const insuranceDelay = myRec.autoplayDelay ?? (Math.floor(Math.random() * 2001) + 1000);
                    console.log(`[Recommendation] Autoplay is ON - will execute ${myRec.action} after ${insuranceDelay}ms delay`);

                    setTimeout(() => {
                      // Re-verify autoplay is still enabled and we're still in insurance phase
                      const currentUser = useAuthStore.getState().user;
                      const currentGameState = useGameStateStore.getState();
                      if (currentUser?.autoPlay !== true) {
                        console.log(`[Recommendation] Autoplay was disabled during delay - skipping auto-execute`);
                        return;
                      }
                      if (!currentGameState.insuranceOffered) {
                        console.log(`[Recommendation] Insurance phase ended during delay - skipping auto-execute`);
                        return;
                      }

                      // Log for diagnostics
                      diagLog.action({
                        seat: myRec.seatPosition,
                        action: myRec.action,
                        source: 'AUTOPLAY',
                      });

                      // Mark decision as pending BEFORE sending - this hides the modal immediately
                      useGameStateStore.getState().setInsuranceDecisionPending(true);

                      const currentSocket = useSocketStore.getState().socket;
                      if (currentSocket) {
                        if (myRec.action === 'INSURANCE') {
                          // Get the bet amount to calculate insurance amount (half the bet)
                          const player = currentGameState.players.get(myRec.seatPosition);
                          const bet = player?.hands[0]?.bet ?? 0;
                          const insuranceAmount = Math.floor(bet / 2);
                          currentSocket.emit('client_message', { type: 'insurance', amount: insuranceAmount });
                          console.log(`[Recommendation] Sent insurance (amount: ${insuranceAmount}) to server`);
                        } else {
                          currentSocket.emit('client_message', { type: 'decline_insurance' });
                          console.log(`[Recommendation] Sent decline_insurance to server`);
                        }
                      }
                    }, insuranceDelay);
                  } else {
                    console.log(`[Recommendation] Autoplay is OFF - BUDDY! can consume insurance recommendation`);
                  }
                }
              }
            }

            // DEAL ANIMATIONS: Await inline so subsequent events in the batch
            // (SCORE_UPDATED, HAND_BUSTED, CHIPS_PAID, etc.) don't process
            // until the card has visually landed. Same pattern as chip animations.
            // Score is calculated AFTER the animation so the score badge doesn't
            // update before the card face is revealed.
            if (event.type === 'CARD_DEALT') {
              const ce = event as BlackjackEvent & { target: 'player' | 'dealer'; seatPosition?: number; handIndex: number; cardIndex: number; card: Card };

              // DIAGNOSTIC: Log all card deals, especially for double down (cardIndex 2)
              console.log(`[CARD_DEALT DIAG] target=${ce.target} seat=${ce.seatPosition} hand=${ce.handIndex} cardIdx=${ce.cardIndex} card=${ce.card.rank}${ce.card.suit}`);

              // Track card for Hi-Lo counting (only face-up cards)
              // Dealer's hole card (faceUp: false) is tracked when revealed
              if (ce.card.faceUp !== false) {
                useCardTrackingStore.getState().trackCard(ce.card);
              }

              // NOTE: Pending bet animations are now awaited at DEALING_STARTED,
              // not here. This ensures they complete before the batch processes
              // CARD_DEALT events.

              // Wait for ALL pending split/double chip animations via orchestrator
              // This ensures split bet chips land before card 2 deals, double chips before double card
              const orchestratorForCard = getAnimationOrchestrator();
              const splitChipCount = orchestratorForCard.getActiveCountByType('SPLIT_CHIPS');
              const doubleChipCount = orchestratorForCard.getActiveCountByType('DOUBLE_CHIPS');
              const splitSlideCount = orchestratorForCard.getActiveCountByType('SPLIT_CARD_SLIDE');

              if (splitChipCount > 0 || doubleChipCount > 0 || splitSlideCount > 0) {
                console.log(`[TIMING] CARD_DEALT waiting for animations: ${splitChipCount} split chips, ${doubleChipCount} double chips, ${splitSlideCount} card slides...`);
                // Wait for all with timeout for resilience
                await Promise.all([
                  orchestratorForCard.waitForTypeWithTimeout('SPLIT_CHIPS', 3000),
                  orchestratorForCard.waitForTypeWithTimeout('DOUBLE_CHIPS', 3000),
                  orchestratorForCard.waitForTypeWithTimeout('SPLIT_CARD_SLIDE', 3000),
                ]);
                console.log(`[TIMING] CARD_DEALT all split/double animations complete, proceeding with deal`);
              }

              // Solo-matching behavior: For split hands, navigate carousel to target hand BEFORE card animation
              // This ensures the player sees the correct hand when their action animates
              if (ce.target === 'player' && ce.seatPosition !== undefined) {
                const gameState = useGameStateStore.getState();
                const isCurrentUser = gameState.mySeatPosition === ce.seatPosition;

                if (isCurrentUser) {
                  const carousel = getSeatCarouselStore();
                  const nav = carousel.getSeatNavigation(ce.seatPosition);

                  // If dealing to a hand that's not currently viewed, rotate carousel first
                  if (nav.totalHands > 1 && nav.viewedHandIndex !== ce.handIndex) {
                    console.log(`[CARD_DEALT PRE] Carousel not on target hand ${ce.handIndex} (viewing ${nav.viewedHandIndex}), rotating BEFORE animation`);

                    // Navigate to the target hand
                    carousel.navigateToHand(ce.seatPosition, ce.handIndex);

                    // Wait for React to process the state update and create the slide promise
                    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

                    // Wait for carousel slide to complete
                    const slidePromise = useAnimationQueueStore.getState().carouselSlidePromise;
                    if (slidePromise) {
                      console.log('[CARD_DEALT PRE] Waiting for carousel slide to complete...');
                      await slidePromise;
                      console.log('[CARD_DEALT PRE] Carousel slide complete');
                    } else {
                      // Fallback: wait for the full slide duration
                      console.log('[CARD_DEALT PRE] No slide promise found, waiting 650ms fallback');
                      await new Promise(r => setTimeout(r, 650));
                    }
                  }
                }
              }

              // Animate card deal via orchestrator
              const seatId = ce.target === 'dealer' ? 'dealer' : String(ce.seatPosition);
              const cardAnimationId = `card-${seatId}-${ce.handIndex}-${ce.cardIndex}`;

              // Pass server-provided duration to animation callback
              const cardDuration = (event as any).duration as number | undefined;
              await getAnimationOrchestrator().animate(cardAnimationId, 'CARD_DEAL', (async () => {
                await onCardDealtRef.current?.(ce.target, ce.seatPosition, ce.handIndex, ce.cardIndex, ce.card, cardDuration);
                useGameStateStore.getState().recalculateScore(ce.target, ce.seatPosition, ce.handIndex);
                // PIXI PURITY: ensureClonesExist removed - Pixi manages card sprites internally
              })());
            }
            if (event.type === 'DEALER_HITS') {
              const de = event as BlackjackEvent & { card: Card };
              useCardTrackingStore.getState().trackCard(de.card);

              const dealer = useGameStateStore.getState().dealer;
              const cardIndex = dealer.cards.length - 1;
              const dealerHitId = `card-dealer-0-${cardIndex}`;
              const dealerHitDuration = (event as any).duration as number | undefined;

              await getAnimationOrchestrator().animate(dealerHitId, 'CARD_DEAL', (async () => {
                await onDealerHitsRef.current?.(cardIndex, dealerHitDuration);
                useGameStateStore.getState().recalculateScore('dealer');
                // PIXI PURITY: ensureClonesExist removed - Pixi manages card sprites internally
              })());
            }
            // Track dealer's hole card when revealed and trigger animation
            // Skip animation if already done during INSURANCE_RESOLVED
            if (event.type === 'DEALER_REVEALS_HOLE') {
              const orchestrator = getAnimationOrchestrator();
              orchestrator.setPhase('DEALER_TURN');
              const re = event as BlackjackEvent & { holeCard: Card; flipDuration?: number };
              if (!dealerRevealAlreadyAnimated) {
                useCardTrackingStore.getState().trackCard(re.holeCard);
                await orchestrator.animate('dealer-reveal-hole', 'DEALER_REVEAL',
                  onDealerRevealsHoleRef.current?.(re.flipDuration) ?? Promise.resolve()
                );
              }
            }

            // Straighten dealer hand when dealer finishes (before chip resolution)
            // Orchestrator handles stale animation cleanup - no timeout needed
            if (event.type === 'DEALER_STANDS') {
              // ===========================================
              // SCORECARD TIMING: Client receives DEALER_STANDS (Step 1 on client)
              // ===========================================
              const dealerStandsClientTime = Date.now();
              console.log(`[SCORECARD TIMING] CLIENT STEP 1: DEALER_STANDS received at ${dealerStandsClientTime} (${new Date(dealerStandsClientTime).toISOString()})`);

              await getAnimationOrchestrator().animate('dealer-stands', 'DEALER_FINISH',
                onDealerStandsRef.current?.() ?? Promise.resolve()
              );
            }
            if (event.type === 'DEALER_BUSTS') {
              // ===========================================
              // SCORECARD TIMING: Client receives DEALER_BUSTS (Step 1 on client)
              // ===========================================
              const dealerBustsClientTime = Date.now();
              console.log(`[SCORECARD TIMING] CLIENT STEP 1: DEALER_BUSTS received at ${dealerBustsClientTime} (${new Date(dealerBustsClientTime).toISOString()})`);

              await getAnimationOrchestrator().animate('dealer-busts', 'DEALER_FINISH',
                onDealerBustsRef.current?.() ?? Promise.resolve()
              );
            }

            // DEALER_BLACKJACK: When dealer has natural BJ with 10-value showing (not Ace),
            // no DEALER_REVEALS_HOLE event is sent. We need to reveal the hole card here.
            // Get cardFlip duration from cached config since there's no DEALER_REVEALS_HOLE event.
            if (event.type === 'DEALER_BLACKJACK') {
              const orchestrator = getAnimationOrchestrator();
              const cachedCardFlip = useAnimationConfigStore.getState().getCachedValue('cardFlip') ?? undefined;
              // Reveal hole card, then straighten hand
              await orchestrator.animate('dealer-bj-reveal', 'DEALER_REVEAL',
                onDealerRevealsHoleRef.current?.(cachedCardFlip) ?? Promise.resolve()
              );
              await orchestrator.animate('dealer-bj-straighten', 'DEALER_FINISH',
                onDealerBlackjackRef.current?.() ?? Promise.resolve()
              );
            }

            // NOTE: SCORE_UPDATED is now handled in the pre-scan phase at batch start
            // to ensure scores are available before card animations complete.

            // Handle TURN_ENDED - clear server recommendation and trigger late sidebet outcome borders
            // Late sidebets (e.g., Mega Hand) don't get SIDEBET_RESOLVED, so we look ahead
            // in the batch for SIDEBET_CHIPS_COLLECTED/PAID events and trigger borders now
            if (event.type === 'TURN_ENDED') {
              const turnEndedEvent = event as BlackjackEvent & { seatPosition: number };
              console.log(`[Sidebet] TURN_ENDED for seat ${turnEndedEvent.seatPosition} - checking for late sidebet resolutions`);

              // Clear server recommendation when turn ends (BUDDY! should stop showing it)
              const gameState = useGameStateStore.getState();
              if (gameState.mySeatPosition === turnEndedEvent.seatPosition) {
                gameState.clearServerRecommendation();
                console.log(`[Recommendation] Cleared server recommendation on TURN_ENDED`);
              }

              // Look ahead in batch for sidebet chip events for this seat
              for (const futureEvent of events) {
                if (futureEvent.type === 'SIDEBET_CHIPS_COLLECTED') {
                  const chipEvent = futureEvent as BlackjackEvent & { seatPosition: number; sidebetType: string; amount: number };
                  if (chipEvent.seatPosition === turnEndedEvent.seatPosition) {
                    console.log(`[Sidebet] TURN_ENDED: Found future SIDEBET_CHIPS_COLLECTED for ${chipEvent.sidebetType} - triggering loss border now`);
                    onSidebetResolvedRef.current?.(
                      chipEvent.seatPosition,
                      chipEvent.sidebetType,
                      'lose',
                      undefined,
                      0
                    );
                  }
                } else if (futureEvent.type === 'SIDEBET_CHIPS_PAID') {
                  const chipEvent = futureEvent as BlackjackEvent & { seatPosition: number; sidebetType: string; amount: number };
                  if (chipEvent.seatPosition === turnEndedEvent.seatPosition) {
                    console.log(`[Sidebet] TURN_ENDED: Found future SIDEBET_CHIPS_PAID for ${chipEvent.sidebetType} - triggering win border now`);
                    onSidebetResolvedRef.current?.(
                      chipEvent.seatPosition,
                      chipEvent.sidebetType,
                      'win',
                      undefined,
                      chipEvent.amount
                    );
                  }
                }
              }
            }

            // Trigger mid-round bust chip animation AFTER outcome label animates in
            if (midRoundBustInfo) {
              // Mark kitty as cleared IMMEDIATELY to prevent HAND_BUSTED from animating again
              useGameStateStore.getState().markKittyCleared(midRoundBustInfo.seatPosition, midRoundBustInfo.handIndex);

              // IMPERATIVE: Show outcome label directly and await its completion
              console.log(`[TIMING] PREMATURE_RESOLUTION bust - showing outcome label imperatively...`);
              if (onShowOutcomeLabelRef.current) {
                await onShowOutcomeLabelRef.current(midRoundBustInfo.seatPosition, midRoundBustInfo.handIndex, 'bust');
              }

              // Block auto-bet during mid-round bust processing
              const orchestrator = getAnimationOrchestrator();
              const animationId = `premature-bust-seat-${midRoundBustInfo.seatPosition}-hand-${midRoundBustInfo.handIndex}`;
              useAnimationQueueStore.getState().setMyHandAnimationsComplete(false);
              console.log(`[TIMING] PREMATURE_RESOLUTION bust (split) - AWAITING mid-round chip animation at ${Date.now()}`);
              await orchestrator.animate(
                animationId,
                'HAND_BUSTED',
                onMidRoundBustRef.current?.(midRoundBustInfo.seatPosition, midRoundBustInfo.handIndex, midRoundBustInfo.betAmount) ?? Promise.resolve()
              );
              console.log(`[TIMING] PREMATURE_RESOLUTION bust (split) - animation complete at ${Date.now()}`);
            }

            // HAND_BUSTED: Trigger chip animation AFTER outcome label animates in
            // EVENT-DRIVEN: Wait for outcome label animation to signal completion
            // This ensures the player sees "BUSTED!" before their chips are taken
            if (handBustedInfo) {
              const gameState = useGameStateStore.getState();
              const kittyKey = `${handBustedInfo.seatPosition}-${handBustedInfo.handIndex}`;

              // Skip if already handled (e.g., by PREMATURE_RESOLUTION for split hands)
              if (gameState.clearedKitties.has(kittyKey)) {
                console.log(`[TIMING] HAND_BUSTED - SKIPPED, kitty already cleared for ${kittyKey} (likely PREMATURE_RESOLUTION)`);
              } else {
                const isOpponent = gameState.mySeatPosition !== handBustedInfo.seatPosition;

                // Mark kitty as cleared IMMEDIATELY before animation
                // This prevents CHIPS_COLLECTED from creating a duplicate LOSER! label
                useGameStateStore.getState().markKittyCleared(handBustedInfo.seatPosition, handBustedInfo.handIndex);

                // IMPERATIVE: Show outcome label directly and await its completion
                // Bypasses React render cycle for reliable event sequencing
                console.log(`[TIMING] HAND_BUSTED - showing outcome label imperatively...`);
                if (onShowOutcomeLabelRef.current) {
                  await onShowOutcomeLabelRef.current(handBustedInfo.seatPosition, handBustedInfo.handIndex, 'bust');
                }
                console.log(`[TIMING] HAND_BUSTED - outcome label animation complete, starting chip animation`);

              const orchestrator = getAnimationOrchestrator();
              const animationId = `bust-seat-${handBustedInfo.seatPosition}-hand-${handBustedInfo.handIndex}`;

              if (isOpponent) {
                // PIXI PURITY: Opponent bust chips animated via Pixi callback
                // CRITICAL: Must await to prevent clearTable from destroying kitty mid-animation
                console.log(`[TIMING] HAND_BUSTED (opponent seat ${handBustedInfo.seatPosition}) - firing Pixi chip animation at ${Date.now()}`);
                const chips = handBustedInfo.betChips || breakdownChips(handBustedInfo.betAmount);
                await orchestrator.animate(animationId, 'HAND_BUSTED', (async () => {
                  if (onOpponentLossToDealerRef.current) {
                    await onOpponentLossToDealerRef.current(handBustedInfo.seatPosition, chips);
                  } else {
                    console.warn(`[useGameEvents] No onOpponentLossToDealer callback for bust animation seat ${handBustedInfo.seatPosition}`);
                  }
                  console.log(`[TIMING] HAND_BUSTED (opponent seat ${handBustedInfo!.seatPosition}) - animation complete at ${Date.now()}`);
                })()).catch(err => {
                  console.error('[TIMING] Opponent bust chip animation error (non-fatal):', err);
                });
              } else {
                // For current user: use the mid-round bust callback via orchestrator
                useAnimationQueueStore.getState().setMyHandAnimationsComplete(false);
                console.log(`[TIMING] HAND_BUSTED (current user) - AWAITING mid-round chip animation at ${Date.now()}`);
                await orchestrator.animate(
                  animationId,
                  'HAND_BUSTED',
                  onMidRoundBustRef.current?.(handBustedInfo.seatPosition, handBustedInfo.handIndex, handBustedInfo.betAmount) ?? Promise.resolve()
                );
                console.log(`[TIMING] HAND_BUSTED (current user) - animation complete at ${Date.now()}`);
              }
              } // Close the else block for clearedKitties check
            }

            // Trigger chip animations AFTER outcome label animates in
            // Wait 400ms (300ms animation + 100ms buffer) for outcome label to display before resolving chips
            // OOO: Outcome label must display BEFORE chips resolve - can't resolve $$$ without knowing outcome
            if (event.type === 'CHIPS_PAID') {
              const chipsPaidEvent = event as BlackjackEvent & { seatPosition: number; handIndex: number; amount: number };
              const gameState = useGameStateStore.getState();
              const isOpponent = gameState.mySeatPosition !== chipsPaidEvent.seatPosition;

              // Outcome labels for ALL seats are now shown by HAND_RESOLVED (emitted simultaneously at dealer resolution)
              // CHIPS_PAID only handles chip animations

              if (!isOpponent) {
                // Route user payout through orchestrator for tracking
                const orchestrator = getAnimationOrchestrator();
                const animationId = `chips-paid-seat-${chipsPaidEvent.seatPosition}-hand-${chipsPaidEvent.handIndex}`;
                console.log(`[TIMING] CHIPS_PAID - AWAITING animation at ${Date.now()}`);
                useAnimationQueueStore.getState().setMyHandAnimationsComplete(false);

                // Balance tracking: register animation start (balance update will be held until complete)
                const clientTableMaster = getClientTableMaster();
                clientTableMaster.registerBalanceAnimation('CHIPS_PAID', chipsPaidEvent.amount, chipsPaidEvent.seatPosition);

                const chipsPaidDuration = (event as any).duration as number | undefined;
                console.log(`[TIMING] CHIPS_PAID - Animating with duration=${chipsPaidDuration ?? 'default'}`);
                await orchestrator.animate(
                  animationId,
                  'CHIPS_PAID',
                  onChipsPaidRef.current?.(chipsPaidEvent.seatPosition, chipsPaidEvent.handIndex, chipsPaidEvent.amount, chipsPaidDuration) ?? Promise.resolve()
                );

                // Balance tracking: animation complete, apply any pending balance update
                clientTableMaster.balanceAnimationComplete();
                console.log(`[TIMING] CHIPS_PAID - animation complete at ${Date.now()}`);
              }
              // NOTE: Opponent CHIPS_PAID is handled separately below (with serialization)
              // Don't call the callback here as it would clear the kitty before our animation runs
            }

            if (event.type === 'CHIPS_COLLECTED') {
              const chipsCollectedEvent = event as BlackjackEvent & { seatPosition: number; handIndex: number; amount: number };
              const gameState = useGameStateStore.getState();
              const isOpponent = gameState.mySeatPosition !== chipsCollectedEvent.seatPosition;

              // Check if kitty was already cleared (e.g., by HAND_BUSTED animation)
              // If already cleared, skip chip animation (outcome label already shown by HAND_RESOLVED)
              const kittyKey = `${chipsCollectedEvent.seatPosition}-${chipsCollectedEvent.handIndex}`;
              if (gameState.clearedKitties.has(kittyKey)) {
                console.log(`[TIMING] CHIPS_COLLECTED - SKIPPED chip animation, kitty already cleared for ${kittyKey} (likely HAND_BUSTED)`);
              } else {
                // Outcome labels for ALL seats are now shown by HAND_RESOLVED (emitted simultaneously at dealer resolution)
                // CHIPS_COLLECTED only handles chip animations

                const orchestrator = getAnimationOrchestrator();
                const animationId = `chips-collected-seat-${chipsCollectedEvent.seatPosition}-hand-${chipsCollectedEvent.handIndex}`;

                if (isOpponent && chipsCollectedEvent.amount > 0) {
                  // PIXI PURITY: Opponent loss animated via Pixi callback (no DOM fallback)
                  const chipsCollectedDuration = (event as any).duration as number | undefined;
                  console.log(`[TIMING] CHIPS_COLLECTED (opponent seat ${chipsCollectedEvent.seatPosition}) - AWAITING Pixi animation at ${Date.now()}, duration=${chipsCollectedDuration ?? 'default'}`);
                  const chips = breakdownChips(chipsCollectedEvent.amount);
                  await orchestrator.animate(animationId, 'CHIPS_COLLECTED', (async () => {
                    if (onOpponentLossToDealerRef.current) {
                      await onOpponentLossToDealerRef.current(chipsCollectedEvent.seatPosition, chips);
                    } else {
                      console.warn(`[useGameEvents] No onOpponentLossToDealer callback for CHIPS_COLLECTED seat ${chipsCollectedEvent.seatPosition}`);
                    }
                    useGameStateStore.getState().markKittyCleared(chipsCollectedEvent.seatPosition, chipsCollectedEvent.handIndex);
                  })());
                  console.log(`[TIMING] CHIPS_COLLECTED (opponent seat ${chipsCollectedEvent.seatPosition}) - animation complete at ${Date.now()}`);
                } else if (!isOpponent) {
                  // For current user: delegate to callback
                  console.log(`[TIMING] CHIPS_COLLECTED - AWAITING animation at ${Date.now()}`);
                  useAnimationQueueStore.getState().setMyHandAnimationsComplete(false);
                  const chipsCollectedDuration = (event as any).duration as number | undefined;
                  console.log(`[TIMING] CHIPS_COLLECTED - Animating with duration=${chipsCollectedDuration ?? 'default'}`);
                  await orchestrator.animate(
                    animationId,
                    'CHIPS_COLLECTED',
                    onChipsCollectedRef.current?.(chipsCollectedEvent.seatPosition, chipsCollectedEvent.handIndex, chipsCollectedEvent.amount, chipsCollectedDuration) ?? Promise.resolve()
                  );
                  console.log(`[TIMING] CHIPS_COLLECTED - animation complete at ${Date.now()}`);
                }
              }
            }

            // ===========================================
            // SEAT_RESOLUTION - Single event for all hand outcomes
            // Frontend iterates hands in REVERSE order (4→3→2→1) for visual presentation
            // NOTE: SEAT_RESOLUTION is a new event type - cast to string for comparison
            // until it's added to BlackjackEvent in shared package
            // ===========================================
            // DIAGNOSTIC: Log event type check for SEAT_RESOLUTION
            if (event.type === 'SEAT_RESOLUTION' || (event.type as string) === 'SEAT_RESOLUTION') {
              console.log(`[DIAG] SEAT_RESOLUTION event detected! type="${event.type}" (typeof: ${typeof event.type})`);
            }
            if ((event.type as string) === 'SEAT_RESOLUTION') {
              const seatResolutionEvent = event as BlackjackEvent & SeatResolutionEvent;
              const gameState = useGameStateStore.getState();
              const isMyResolution = gameState.mySeatPosition === seatResolutionEvent.seatPosition;

              console.log(`[SEAT_RESOLUTION] Seat ${seatResolutionEvent.seatPosition}: ${seatResolutionEvent.hands.length} hands, isMe=${isMyResolution}`);

              if (isMyResolution && onSeatResolutionRef.current) {
                // Route through orchestrator for tracking
                const orchestrator = getAnimationOrchestrator();
                const animationId = `seat-resolution-${seatResolutionEvent.seatPosition}`;

                console.log(`[SEAT_RESOLUTION] Processing ${seatResolutionEvent.hands.length} hands in reverse order`);
                useAnimationQueueStore.getState().setMyHandAnimationsComplete(false);

                await orchestrator.animate(
                  animationId,
                  'SEAT_RESOLUTION',
                  onSeatResolutionRef.current(seatResolutionEvent)
                );

                console.log(`[SEAT_RESOLUTION] Complete for seat ${seatResolutionEvent.seatPosition}`);
              }
              // NOTE: Opponent SEAT_RESOLUTION would need similar handling if implemented
            }

            if (event.type === 'HAND_RESOLVED') {
              const resolvedEvent = event as BlackjackEvent & { seatPosition: number; handIndex: number; outcome: string; betAmount: number };
              const gameState = useGameStateStore.getState();
              const isOpponent = gameState.mySeatPosition !== resolvedEvent.seatPosition;

              // Outcome labels for ALL seats are now shown by SCORECARD_UPDATE (emitted simultaneously at dealer resolution)
              // HAND_RESOLVED only handles chip animations

              // Handle push chip return animation
              if (resolvedEvent.outcome === 'push') {
                const orchestrator = getAnimationOrchestrator();
                const animationId = `chips-returned-seat-${resolvedEvent.seatPosition}-hand-${resolvedEvent.handIndex}`;

                if (!isOpponent) {
                  // Only block betting for current user's animations
                  console.log(`[TIMING] CHIPS_RETURNED (push) - AWAITING animation at ${Date.now()}`);
                  useAnimationQueueStore.getState().setMyHandAnimationsComplete(false);

                  // Balance tracking: register animation start (balance update will be held until complete)
                  const clientTableMaster = getClientTableMaster();
                  clientTableMaster.registerBalanceAnimation('CHIPS_RETURNED', resolvedEvent.betAmount || 0, resolvedEvent.seatPosition);

                  await orchestrator.animate(
                    animationId,
                    'CHIPS_RETURNED',
                    onChipsReturnedRef.current?.(resolvedEvent.seatPosition, resolvedEvent.handIndex, resolvedEvent.betAmount || 0) ?? Promise.resolve()
                  );

                  // Balance tracking: animation complete, apply any pending balance update
                  clientTableMaster.balanceAnimationComplete();
                  console.log(`[TIMING] CHIPS_RETURNED (push) - animation complete at ${Date.now()}`);
                } else {
                  // For opponents: just call the callback without blocking betting
                  await orchestrator.animate(
                    animationId,
                    'CHIPS_RETURNED',
                    onChipsReturnedRef.current?.(resolvedEvent.seatPosition, resolvedEvent.handIndex, resolvedEvent.betAmount || 0) ?? Promise.resolve()
                  );
                }
              }
            }

            // Handle INSURANCE_DECISION - animate via orchestrator
            if (event.type === 'INSURANCE_DECISION') {
              const insuranceEvent = event as BlackjackEvent & { seatPosition: number; accepted: boolean; amount?: number };

              // Hide insurance badge immediately when any player decides (accept or decline)
              // This is more reliable than relying on the useEffect in PixiTablePage
              onInsuranceDecisionRef.current?.(insuranceEvent.seatPosition);

              if (insuranceEvent.accepted && insuranceEvent.amount) {
                const gameState = useGameStateStore.getState();
                const isOpponent = gameState.mySeatPosition !== insuranceEvent.seatPosition;
                const orchestrator = getAnimationOrchestrator();
                const animationId = `insurance-seat-${insuranceEvent.seatPosition}`;

                if (isOpponent) {
                  // PIXI-ONLY: Opponent insurance via Pixi callback
                  const chips = breakdownChips(insuranceEvent.amount);
                  if (onOpponentInsuranceAcceptedRef.current) {
                    await orchestrator.animate(animationId, 'INSURANCE_CHIPS',
                      onOpponentInsuranceAcceptedRef.current(insuranceEvent.seatPosition, insuranceEvent.amount, chips)
                    );
                  } else {
                    console.warn(`[useGameEvents] No onOpponentInsuranceAccepted callback for seat ${insuranceEvent.seatPosition}`);
                  }
                } else {
                  // Balance tracking: register animation start (balance update will be held until complete)
                  const clientTableMaster = getClientTableMaster();
                  clientTableMaster.registerBalanceAnimation('INSURANCE_DECISION', insuranceEvent.amount, insuranceEvent.seatPosition);

                  await orchestrator.animate(animationId, 'INSURANCE_CHIPS',
                    onInsuranceAcceptedRef.current?.(insuranceEvent.seatPosition, insuranceEvent.amount) ?? Promise.resolve()
                  );

                  // Balance tracking: animation complete, apply any pending balance update
                  clientTableMaster.balanceAnimationComplete();
                }
              }
            }

            // Handle ACTION_TAKEN with DOUBLE - animate via orchestrator
            if (event.type === 'ACTION_TAKEN') {
              const actionEvent = event as BlackjackEvent & { seatPosition: number; handIndex: number; action: PlayerAction; betAdjustment?: number };
              if (actionEvent.action === PlayerAction.DOUBLE && actionEvent.betAdjustment) {
                const gameState = useGameStateStore.getState();
                const isOpponent = gameState.mySeatPosition !== actionEvent.seatPosition;
                const orchestrator = getAnimationOrchestrator();
                const animationId = `double-seat-${actionEvent.seatPosition}`;

                if (isOpponent) {
                  // PIXI-ONLY: Opponent double via Pixi callback
                  const chips = breakdownChips(actionEvent.betAdjustment);
                  if (onOpponentDoubleDownRef.current) {
                    await orchestrator.animate(animationId, 'DOUBLE_CHIPS',
                      onOpponentDoubleDownRef.current(actionEvent.seatPosition, actionEvent.handIndex, chips)
                    );
                  } else {
                    console.warn(`[useGameEvents] No onOpponentDoubleDown callback for seat ${actionEvent.seatPosition}`);
                  }
                } else {
                  // Current user double
                  // Balance tracking: register animation start (balance update will be held until complete)
                  const clientTableMaster = getClientTableMaster();
                  clientTableMaster.registerBalanceAnimation('ACTION_TAKEN_DOUBLE', actionEvent.betAdjustment, actionEvent.seatPosition);

                  await orchestrator.animate(animationId, 'DOUBLE_CHIPS',
                    onDoubleDownRef.current?.(actionEvent.seatPosition, actionEvent.handIndex, actionEvent.betAdjustment) ?? Promise.resolve()
                  );

                  // Balance tracking: animation complete, apply any pending balance update
                  clientTableMaster.balanceAnimationComplete();
                }
              }
            }

            // Handle HAND_SPLIT - animate via orchestrator
            // DIAGNOSTIC: Log event type check for HAND_SPLIT
            if (event.type === 'HAND_SPLIT') {
              console.log(`[DIAG] HAND_SPLIT event detected! seat=${(event as any).seatPosition} originalHand=${(event as any).originalHandIndex} newHand=${(event as any).newHandIndex}`);
            }
            if (event.type === 'HAND_SPLIT') {
              const splitEvent = event as BlackjackEvent & { seatPosition: number; originalHandIndex: number; newHandIndex: number; additionalBet: number };
              const gameState = useGameStateStore.getState();
              const isOpponent = gameState.mySeatPosition !== splitEvent.seatPosition;
              const orchestrator = getAnimationOrchestrator();
              const animationId = `split-seat-${splitEvent.seatPosition}-hand-${splitEvent.newHandIndex}`;

              if (isOpponent) {
                // PIXI-ONLY: Opponent split via Pixi callback
                const chips = breakdownChips(splitEvent.additionalBet);
                if (onOpponentSplitRef.current) {
                  await orchestrator.animate(animationId, 'SPLIT_CHIPS',
                    onOpponentSplitRef.current(splitEvent.seatPosition, splitEvent.newHandIndex, chips)
                  );
                } else {
                  console.warn(`[useGameEvents] No onOpponentSplit callback for seat ${splitEvent.seatPosition}`);
                }
              } else {
                // Current user split - MUST await to prevent autoplay chaos
                // (matches current user double at line ~1263)
                // Balance tracking: register animation start (balance update will be held until complete)
                const clientTableMaster = getClientTableMaster();
                clientTableMaster.registerBalanceAnimation('HAND_SPLIT', splitEvent.additionalBet, splitEvent.seatPosition);

                await orchestrator.animate(animationId, 'SPLIT_CHIPS',
                  onSplitRef.current?.(splitEvent.seatPosition, splitEvent.originalHandIndex, splitEvent.newHandIndex, splitEvent.additionalBet) ?? Promise.resolve()
                );

                // Balance tracking: animation complete, apply any pending balance update
                clientTableMaster.balanceAnimationComplete();
              }
            }

            // Handle HAND_SURRENDERED
            if (event.type === 'HAND_SURRENDERED') {
              const surrenderEvent = event as BlackjackEvent & { seatPosition: number; handIndex: number; refundAmount: number };
              const gameState = useGameStateStore.getState();
              const player = gameState.players.get(surrenderEvent.seatPosition);
              const hand = player?.hands[surrenderEvent.handIndex];
              const betAmount = hand?.bet || surrenderEvent.refundAmount * 2;
              const isOpponent = gameState.mySeatPosition !== surrenderEvent.seatPosition;

              // Mark kitty as cleared FIRST to prevent CHIPS_COLLECTED from showing LOSER! label
              const kittyKey = `${surrenderEvent.seatPosition}-${surrenderEvent.handIndex}`;
              useGameStateStore.getState().markKittyCleared(surrenderEvent.seatPosition, surrenderEvent.handIndex);
              console.log(`[TIMING] HAND_SURRENDERED - marked kitty cleared for ${kittyKey} to prevent LOSER! label`);

              // IMPERATIVE: Show SURRENDER outcome label directly and await its completion
              console.log(`[TIMING] HAND_SURRENDERED - showing outcome label imperatively (surrender)...`);
              if (onShowOutcomeLabelRef.current) {
                await onShowOutcomeLabelRef.current(surrenderEvent.seatPosition, surrenderEvent.handIndex, 'surrender');
              }

              if (isOpponent) {
                // PIXI-ONLY: Opponent surrender via Pixi callback
                console.log(`[TIMING] HAND_SURRENDERED (opponent seat ${surrenderEvent.seatPosition}) - firing animation at ${Date.now()}`);
                const lossAmount = betAmount - surrenderEvent.refundAmount;
                const lossChips = breakdownChips(lossAmount);
                const refundChips = breakdownChips(surrenderEvent.refundAmount);

                if (onOpponentSurrenderRef.current) {
                  await onOpponentSurrenderRef.current(surrenderEvent.seatPosition, surrenderEvent.handIndex, lossChips, refundChips);
                  console.log(`[TIMING] HAND_SURRENDERED (opponent seat ${surrenderEvent.seatPosition}) - animation complete at ${Date.now()}`);
                } else {
                  console.warn(`[useGameEvents] No onOpponentSurrender callback for seat ${surrenderEvent.seatPosition}`);
                }
              } else {
                // For current user: use the callback for full animation control
                console.log(`[TIMING] HAND_SURRENDERED - AWAITING animation at ${Date.now()}`, { betAmount, refundAmount: surrenderEvent.refundAmount });
                await onSurrenderRef.current?.(surrenderEvent.seatPosition, surrenderEvent.handIndex, betAmount, surrenderEvent.refundAmount);
                console.log(`[TIMING] HAND_SURRENDERED - animation complete at ${Date.now()}`);
              }
            }

            // Handle SEAT_RAKE - server commands client to clear previous round's cards for a seat
            // This fires BEFORE BET_CONFIRMED to ensure proper order: rake → bet chips animate
            if (event.type === 'SEAT_RAKE') {
              const rakeEvent = event as BlackjackEvent & { seatPosition: number };
              const gameState = useGameStateStore.getState();
              const mySeat = gameState.mySeatPosition;
              const isOpponent = mySeat !== null && mySeat !== undefined && mySeat !== rakeEvent.seatPosition;
              const orchestrator = getAnimationOrchestrator();
              const animationId = `rake-seat-${rakeEvent.seatPosition}`;

              if (isOpponent) {
                // PIXI-ONLY: Opponent seat rake via Pixi callback
                if (onOpponentSeatRakeRef.current) {
                  await orchestrator.animate(animationId, 'RAKE',
                    onOpponentSeatRakeRef.current(rakeEvent.seatPosition)
                  );
                } else {
                  // No callback = no-op (Pixi table must provide callback)
                  console.warn(`[useGameEvents] No onOpponentSeatRake callback for seat ${rakeEvent.seatPosition}`);
                }
              } else {
                // Current user seat rake - delegate to callback
                if (onSeatRakeRef.current) {
                  await orchestrator.animate(animationId, 'RAKE',
                    onSeatRakeRef.current(rakeEvent.seatPosition)
                  );
                } else {
                  console.warn(`[useGameEvents] No onSeatRake callback for seat ${rakeEvent.seatPosition}`);
                }
              }
            }

            // Handle DEALER_RAKE - server commands client to clear dealer cards
            // Fires after ALL players have raked. Deal starts after this completes.
            if (event.type === 'DEALER_RAKE') {
              console.log('[useGameEvents] DEALER_RAKE received - raking dealer cards');
              const orchestrator = getAnimationOrchestrator();
              const animationId = 'rake-dealer';

              if (onDealerRakeRef.current) {
                await orchestrator.animate(animationId, 'RAKE',
                  onDealerRakeRef.current()
                );
              } else {
                console.warn('[useGameEvents] No onDealerRake callback provided');
              }
            }

            // Handle BET_CONFIRMED for ALL players - animate bet chips to kitty
            // SEAT_RAKE has already cleared previous round's cards, so we just animate chips
            // Animation promise is tracked so CARD_DEALT waits for all bets to land first
            if (event.type === 'BET_CONFIRMED') {
              const betEvent = event as BlackjackEvent & { seatPosition: number; amount: number; betChips?: number[] };
              const gameState = useGameStateStore.getState();
              const mySeat = gameState.mySeatPosition;

              // Debug logging to diagnose first-hand issues
              console.log(`[TIMING] BET_CONFIRMED: seat=${betEvent.seatPosition}, mySeat=${mySeat}, amount=${betEvent.amount}, betChips=${JSON.stringify(betEvent.betChips)}`);

              // Check if this is the current user's bet
              // mySeat could be null/undefined on first join, so check explicitly
              const isOpponent = mySeat !== null && mySeat !== undefined && mySeat !== betEvent.seatPosition;

              if (betEvent.amount > 0) {
                const chips = betEvent.betChips || breakdownChips(betEvent.amount);
                const orchestrator = getAnimationOrchestrator();

                // CRITICAL: Wait for RAKE animations to complete before starting bet animation
                // This prevents bet chips from animating during rake (split hands can take 5+ seconds)
                await orchestrator.waitForType('RAKE');
                console.log(`[TIMING] BET_CONFIRMED: RAKE complete, starting bet animation for seat ${betEvent.seatPosition}`);
                const animationId = `bet-seat-${betEvent.seatPosition}`;

                if (isOpponent) {
                  // PIXI PURITY: Opponent bet animated via Pixi callback (no DOM fallback)
                  const opponentAnimation = (async () => {
                    if (onOpponentBetConfirmedRef.current) {
                      await onOpponentBetConfirmedRef.current(betEvent.seatPosition, betEvent.amount, chips);
                    } else {
                      console.warn(`[useGameEvents] No onOpponentBetConfirmed callback for seat ${betEvent.seatPosition}`);
                    }
                    useGameStateStore.getState().endOpponentBetAnimation(betEvent.seatPosition);
                  })();

                  // Track via orchestrator
                  orchestrator.animate(animationId, 'BET_CHIPS', opponentAnimation);
                } else {
                  // Balance tracking: register animation start (balance update will be held until complete)
                  const clientTableMaster = getClientTableMaster();
                  clientTableMaster.registerBalanceAnimation('BET_CONFIRMED', betEvent.amount, betEvent.seatPosition);

                  // Current user bet - animate via callback through orchestrator
                  const userAnimation = (async () => {
                    try {
                      if (onBetConfirmedRef.current) {
                        await onBetConfirmedRef.current(betEvent.seatPosition, betEvent.amount, chips);
                      }
                    } finally {
                      // Balance tracking: animation complete, apply any pending balance update
                      clientTableMaster.balanceAnimationComplete();
                    }
                  })();

                  // Track via orchestrator
                  orchestrator.animate(animationId, 'BET_CHIPS', userAnimation);
                }
              }
            }

            // Handle CHIPS_PAID for opponents - animate from dealer to kitty, then kitty to avatar
            // For split hands, serialize animations per seat (hand 0 completes before hand 1 starts)
            // OOO: Wait for outcome label to animate in before resolving chips
            if (event.type === 'CHIPS_PAID') {
              const chipsPaidEvent = event as BlackjackEvent & { seatPosition: number; handIndex: number; amount: number };
              const gameState = useGameStateStore.getState();
              const isOpponent = gameState.mySeatPosition !== chipsPaidEvent.seatPosition;
              if (isOpponent && chipsPaidEvent.amount > 0) {
                console.log(`[TIMING] CHIPS_PAID (opponent seat ${chipsPaidEvent.seatPosition}, hand ${chipsPaidEvent.handIndex}) - queuing animation at ${Date.now()}`);
                const chips = breakdownChips(chipsPaidEvent.amount);
                // Get the player's original bet chips for the full kitty-to-avatar animation
                const player = gameState.players.get(chipsPaidEvent.seatPosition);
                const hand = player?.hands[chipsPaidEvent.handIndex];
                const betChips = hand?.betChips || [];
                const allChips = [...betChips, ...chips]; // Original bet + payout

                const orchestrator = getAnimationOrchestrator();
                const animationId = `opp-payout-seat-${chipsPaidEvent.seatPosition}-hand-${chipsPaidEvent.handIndex}`;

                // Extract server-provided duration for chip animations
                const chipDuration = (event as any).duration as number | undefined;

                // Wait for any previous payouts for this seat (for split hand serialization)
                await orchestrator.waitForIdPrefix(`opp-payout-seat-${chipsPaidEvent.seatPosition}-`);

                // Outcome label already shown by HAND_RESOLVED handler (emitted simultaneously at dealer resolution)
                console.log(`[TIMING] CHIPS_PAID (opponent seat ${chipsPaidEvent.seatPosition}, hand ${chipsPaidEvent.handIndex}) - starting animation at ${Date.now()}, duration=${chipDuration ?? 'default'}`);

                // PIXI PURITY: Opponent payout animated via Pixi callbacks (no DOM fallback)
                // CRITICAL: Must await to prevent clearTable from destroying kitty mid-animation
                await orchestrator.animate(animationId, 'CHIPS_PAID', (async () => {
                  try {
                    if (onOpponentPayoutToKittyRef.current && onOpponentKittyToAvatarRef.current) {
                      // Step 1: Dealer → Kitty (payout chips land at kitty)
                      await onOpponentPayoutToKittyRef.current(chipsPaidEvent.seatPosition, chips);
                      // Step 2: Kitty → Avatar (all chips including original bet go to avatar)
                      await onOpponentKittyToAvatarRef.current(chipsPaidEvent.seatPosition, allChips);
                    } else {
                      console.warn(`[useGameEvents] No onOpponentPayoutToKitty/onOpponentKittyToAvatar callbacks for seat ${chipsPaidEvent.seatPosition}`);
                    }
                    // Clear the kitty after animation completes
                    useGameStateStore.getState().markKittyCleared(chipsPaidEvent.seatPosition, chipsPaidEvent.handIndex);
                    console.log(`[TIMING] CHIPS_PAID (opponent seat ${chipsPaidEvent.seatPosition}, hand ${chipsPaidEvent.handIndex}) - animation complete at ${Date.now()}`);
                  } catch (err) {
                    console.error('[TIMING] Opponent payout animation error (non-fatal):', err);
                  }
                })());
              }
            }

            // Handle HAND_RESOLVED (push) for opponents - animate kitty to avatar (no dealer payout)
            // For split hands, serialize animations per seat
            // OOO: Wait for outcome label to animate in before resolving chips
            if (event.type === 'HAND_RESOLVED') {
              const resolvedEvent = event as BlackjackEvent & { seatPosition: number; handIndex: number; outcome: string; betAmount?: number };
              if (resolvedEvent.outcome === 'push') {
                const gameState = useGameStateStore.getState();
                const isOpponent = gameState.mySeatPosition !== resolvedEvent.seatPosition;
                if (isOpponent) {
                  console.log(`[TIMING] HAND_RESOLVED push (opponent seat ${resolvedEvent.seatPosition}, hand ${resolvedEvent.handIndex}) - queuing animation at ${Date.now()}`);
                  // Get the player's bet chips for the animation
                  const player = gameState.players.get(resolvedEvent.seatPosition);
                  const hand = player?.hands[resolvedEvent.handIndex];
                  const betChips = hand?.betChips || breakdownChips(resolvedEvent.betAmount || hand?.bet || 0);
                  const orchestrator = getAnimationOrchestrator();
                  const animationId = `opp-push-seat-${resolvedEvent.seatPosition}-hand-${resolvedEvent.handIndex}`;

                  // Wait for any previous payouts/pushes for this seat (split hand serialization)
                  await orchestrator.waitForIdPrefix(`opp-payout-seat-${resolvedEvent.seatPosition}-`);
                  await orchestrator.waitForIdPrefix(`opp-push-seat-${resolvedEvent.seatPosition}-`);

                  // Outcome label already shown by HAND_RESOLVED handler above (emitted simultaneously at dealer resolution)

                  console.log(`[TIMING] HAND_RESOLVED push (opponent seat ${resolvedEvent.seatPosition}, hand ${resolvedEvent.handIndex}) - starting animation at ${Date.now()}`);

                  // PIXI PURITY: Opponent push animated via Pixi callback (no DOM fallback)
                  // CRITICAL: Must await to prevent clearTable from destroying kitty mid-animation
                  await orchestrator.animate(animationId, 'CHIPS_RETURNED', (async () => {
                    try {
                      if (onOpponentKittyToAvatarRef.current) {
                        await onOpponentKittyToAvatarRef.current(resolvedEvent.seatPosition, betChips);
                      } else {
                        console.warn(`[useGameEvents] No onOpponentKittyToAvatar callback for push seat ${resolvedEvent.seatPosition}`);
                      }
                      useGameStateStore.getState().markKittyCleared(resolvedEvent.seatPosition, resolvedEvent.handIndex);
                      console.log(`[TIMING] HAND_RESOLVED push (opponent seat ${resolvedEvent.seatPosition}, hand ${resolvedEvent.handIndex}) - animation complete`);
                    } catch (err) {
                      console.error('[TIMING] Opponent push animation error (non-fatal):', err);
                    }
                  })());
                }
              }
            }

            // ===========================================
            // SIDEBET EVENTS
            // ===========================================

            // Handle SIDEBET_PLACED - animate chips to sidebet icon
            // Order of operations: bet animation completes → sidebets animate one at a time
            if (event.type === 'SIDEBET_PLACED') {
              const sidebetEvent = event as BlackjackEvent & {
                seatPosition: number;
                sidebetType: string;
                amount: number;
                rounds: number;
              };
              console.log(`[Sidebet] SIDEBET_PLACED - seat ${sidebetEvent.seatPosition}, type: ${sidebetEvent.sidebetType}, amount: ${sidebetEvent.amount}`);

              // Track that a sidebet was placed this round
              sidebetsPlacedThisRoundRef.current++;

              const gameState = useGameStateStore.getState();
              const isMyEvent = gameState.mySeatPosition === sidebetEvent.seatPosition;

              // Balance tracking: only track for current user's sidebet placements
              if (isMyEvent) {
                const clientTableMaster = getClientTableMaster();
                clientTableMaster.registerBalanceAnimation('SIDEBET_PLACED', sidebetEvent.amount, sidebetEvent.seatPosition);
              }

              const orchestrator = getAnimationOrchestrator();
              const animationId = `sidebet-placed-seat-${sidebetEvent.seatPosition}-${sidebetEvent.sidebetType}`;

              // Wait for THIS SEAT's primary bet animation to complete first
              // Each seat's bets are independent - no cross-player dependencies
              console.log(`[Sidebet] Waiting for seat ${sidebetEvent.seatPosition}'s bet animation before ${sidebetEvent.sidebetType}...`);
              await orchestrator.waitForIdPrefixWithTimeout(`bet-seat-${sidebetEvent.seatPosition}`, 2000);

              // PARALLEL SIDEBETS: All sidebets for this seat animate together after primary bet
              // NOTE: To make sidebets animate SEQUENTIALLY within a seat, uncomment the line below:
              // await orchestrator.waitForIdPrefixWithTimeout(`sidebet-placed-seat-${sidebetEvent.seatPosition}`, 2000);

              // Now animate this sidebet via orchestrator
              // Extract server-provided duration and stagger from event
              const sidebetPlacedDuration = (event as any).duration as number | undefined;
              const sidebetPlacedStagger = (event as any).stagger as number | undefined;
              console.log(`[Sidebet] Animating ${sidebetEvent.sidebetType}... duration=${sidebetPlacedDuration ?? 'default'}, stagger=${sidebetPlacedStagger ?? 'default'}`);
              await orchestrator.animate(
                animationId,
                'SIDEBET_PLACED',
                onSidebetPlacedRef.current?.(sidebetEvent.seatPosition, sidebetEvent.sidebetType, sidebetEvent.amount, sidebetEvent.rounds, sidebetPlacedDuration, sidebetPlacedStagger) ?? Promise.resolve()
              );

              // Balance tracking: animation complete
              if (isMyEvent) {
                const clientTableMaster = getClientTableMaster();
                clientTableMaster.balanceAnimationComplete();
              }

              console.log(`[Sidebet] ${sidebetEvent.sidebetType} animation complete`);
            }

            // Handle SIDEBET_RESOLVED - visual feedback after player's 2nd card lands
            if (event.type === 'SIDEBET_RESOLVED') {
              const resolvedEvent = event as BlackjackEvent & {
                seatPosition: number;
                sidebetType: string;
                outcome: 'win' | 'lose';
                winType?: string;
                netResult: number;
              };
              console.log(`[Sidebet] SIDEBET_RESOLVED - seat ${resolvedEvent.seatPosition}, type: ${resolvedEvent.sidebetType}, outcome: ${resolvedEvent.outcome}${resolvedEvent.winType ? ` (${resolvedEvent.winType})` : ''}`);

              const gameState = useGameStateStore.getState();
              const isMyEvent = gameState.mySeatPosition === resolvedEvent.seatPosition;

              // Wait for player's 2nd card animation to complete before showing outcome border
              // Card animation ID format: card-{seatPosition}-{handIndex}-{cardIndex}
              // Player's 2nd card is cardIndex 1 (0-indexed)
              const player2ndCardPrefix = `card-${resolvedEvent.seatPosition}-0-1`;
              console.log(`[Sidebet] Waiting for player's 2nd card animation: ${player2ndCardPrefix}`);

              const orchestrator = getAnimationOrchestrator();
              await orchestrator.waitForIdPrefixWithTimeout(player2ndCardPrefix, 3000);

              console.log(`[Sidebet] Player's 2nd card landed, showing outcome border for ${resolvedEvent.sidebetType}`);

              // Update sidebet resolution state for reconciler visual feedback
              // This enables green (win) or red (lose) border on sidebet icons
              if (isMyEvent) {
                // For current user: update mySidebets (reconciler uses this)
                gameState.updateSidebetResolution(
                  resolvedEvent.sidebetType,
                  resolvedEvent.outcome,
                  resolvedEvent.winType,
                  resolvedEvent.netResult
                );
              } else {
                // For other players: update player.activeSidebets
                gameState.updatePlayerSidebetResolution(
                  resolvedEvent.seatPosition,
                  resolvedEvent.sidebetType,
                  resolvedEvent.outcome
                );
              }

              onSidebetResolvedRef.current?.(
                resolvedEvent.seatPosition,
                resolvedEvent.sidebetType,
                resolvedEvent.outcome,
                resolvedEvent.winType,
                resolvedEvent.netResult
              );
            }

            // Handle SIDEBET_CHIPS_PAID - animate sidebet win payout
            if (event.type === 'SIDEBET_CHIPS_PAID') {
              const chipsPaidEvent = event as BlackjackEvent & {
                seatPosition: number;
                sidebetType: string;
                amount: number;
                chipBreakdown: ChipBreakdown;
                isFinal?: boolean;
              };
              console.log(`[Sidebet] SIDEBET_CHIPS_PAID - seat ${chipsPaidEvent.seatPosition}, type: ${chipsPaidEvent.sidebetType}, amount: ${chipsPaidEvent.amount}, totalChips: ${chipsPaidEvent.chipBreakdown?.totalChips ?? 'N/A'}, isFinal: ${chipsPaidEvent.isFinal ?? false}`);

              const gameState = useGameStateStore.getState();
              const isMyEvent = gameState.mySeatPosition === chipsPaidEvent.seatPosition;

              // Balance tracking: only track for current user's sidebet payouts
              if (isMyEvent) {
                const clientTableMaster = getClientTableMaster();
                clientTableMaster.registerBalanceAnimation('SIDEBET_CHIPS_PAID', chipsPaidEvent.amount, chipsPaidEvent.seatPosition);
              }

              // Trigger win border for late-resolving sidebets (e.g., Mega Hand)
              // that don't get SIDEBET_RESOLVED events
              console.log(`[Sidebet] SIDEBET_CHIPS_PAID triggering win border for ${chipsPaidEvent.sidebetType}`);
              onSidebetResolvedRef.current?.(
                chipsPaidEvent.seatPosition,
                chipsPaidEvent.sidebetType,
                'win',
                undefined,
                chipsPaidEvent.amount
              );

              const orchestrator = getAnimationOrchestrator();
              const animationId = `sidebet-paid-seat-${chipsPaidEvent.seatPosition}-${chipsPaidEvent.sidebetType}`;

              // Wait for THIS SEAT's primary bet resolution before animating sidebet payout
              // Each seat's bets are independent - no cross-player dependencies
              // (waitForIdPrefixWithTimeout returns immediately if no matching animations)
              console.log(`[Sidebet] Waiting for seat ${chipsPaidEvent.seatPosition}'s primary bet resolution...`);
              await orchestrator.waitForIdPrefixWithTimeout(`chips-paid-seat-${chipsPaidEvent.seatPosition}`, 2000);
              await orchestrator.waitForIdPrefixWithTimeout(`chips-collected-seat-${chipsPaidEvent.seatPosition}`, 2000);
              await orchestrator.waitForIdPrefixWithTimeout(`chips-returned-seat-${chipsPaidEvent.seatPosition}`, 2000);

              // PARALLEL SIDEBETS: All sidebet payouts for this seat animate together after primary bet
              // NOTE: To make sidebet payouts SEQUENTIAL within a seat, uncomment the line below:
              // await orchestrator.waitForIdPrefixWithTimeout(`sidebet-paid-seat-${chipsPaidEvent.seatPosition}`, 2000);

              const sidebetPaidDuration = (event as any).duration as number | undefined;
              console.log(`[Sidebet] Animating SIDEBET_CHIPS_PAID... duration=${sidebetPaidDuration ?? 'default'}, chipBreakdown totalChips: ${chipsPaidEvent.chipBreakdown?.totalChips ?? 'N/A'}, isFinal: ${chipsPaidEvent.isFinal ?? false}`);
              await orchestrator.animate(
                animationId,
                'SIDEBET_CHIPS_PAID',
                onSidebetChipsPaidRef.current?.(chipsPaidEvent.seatPosition, chipsPaidEvent.sidebetType, chipsPaidEvent.amount, chipsPaidEvent.chipBreakdown, sidebetPaidDuration, chipsPaidEvent.isFinal) ?? Promise.resolve()
              );

              // Balance tracking: animation complete
              if (isMyEvent) {
                const clientTableMaster = getClientTableMaster();
                clientTableMaster.balanceAnimationComplete();
              }
            }

            // Handle SIDEBET_CHIPS_COLLECTED - animate sidebet loss
            if (event.type === 'SIDEBET_CHIPS_COLLECTED') {
              const chipsCollectedEvent = event as BlackjackEvent & {
                seatPosition: number;
                sidebetType: string;
                amount: number;
                isFinal?: boolean;
              };
              console.log(`[Sidebet] SIDEBET_CHIPS_COLLECTED - seat ${chipsCollectedEvent.seatPosition}, type: ${chipsCollectedEvent.sidebetType}, amount: ${chipsCollectedEvent.amount}, isFinal: ${chipsCollectedEvent.isFinal ?? false}`);

              // Trigger loss border for late-resolving sidebets (e.g., Mega Hand)
              // that don't get SIDEBET_RESOLVED events
              console.log(`[Sidebet] SIDEBET_CHIPS_COLLECTED triggering loss border for ${chipsCollectedEvent.sidebetType}`);
              onSidebetResolvedRef.current?.(
                chipsCollectedEvent.seatPosition,
                chipsCollectedEvent.sidebetType,
                'lose',
                undefined,
                0
              );

              const orchestrator = getAnimationOrchestrator();
              const animationId = `sidebet-collected-seat-${chipsCollectedEvent.seatPosition}-${chipsCollectedEvent.sidebetType}`;

              // Wait for THIS SEAT's primary bet resolution before animating sidebet loss
              // Each seat's bets are independent - no cross-player dependencies
              // (waitForIdPrefixWithTimeout returns immediately if no matching animations)
              console.log(`[Sidebet] Waiting for seat ${chipsCollectedEvent.seatPosition}'s primary bet resolution...`);
              await orchestrator.waitForIdPrefixWithTimeout(`chips-paid-seat-${chipsCollectedEvent.seatPosition}`, 2000);
              await orchestrator.waitForIdPrefixWithTimeout(`chips-collected-seat-${chipsCollectedEvent.seatPosition}`, 2000);
              await orchestrator.waitForIdPrefixWithTimeout(`chips-returned-seat-${chipsCollectedEvent.seatPosition}`, 2000);

              // PARALLEL SIDEBETS: All sidebet losses for this seat animate together after primary bet
              // NOTE: To make sidebet losses SEQUENTIAL within a seat, uncomment the line below:
              // await orchestrator.waitForIdPrefixWithTimeout(`sidebet-collected-seat-${chipsCollectedEvent.seatPosition}`, 2000);

              const sidebetCollectedDuration = (event as any).duration as number | undefined;
              const sidebetCollectedStagger = (event as any).stagger as number | undefined;
              console.log(`[Sidebet] Animating SIDEBET_CHIPS_COLLECTED... duration=${sidebetCollectedDuration ?? 'default'}, stagger=${sidebetCollectedStagger ?? 'default'}, isFinal: ${chipsCollectedEvent.isFinal ?? false}`);
              await orchestrator.animate(
                animationId,
                'SIDEBET_CHIPS_COLLECTED',
                onSidebetChipsCollectedRef.current?.(chipsCollectedEvent.seatPosition, chipsCollectedEvent.sidebetType, chipsCollectedEvent.amount, sidebetCollectedDuration, sidebetCollectedStagger, chipsCollectedEvent.isFinal) ?? Promise.resolve()
              );
            }

            // Handle SIDEBET_REMOVED - behavior depends on pending vs live state
            // NO-REFUNDS MODEL: committed bets always ride to conclusion
            if (event.type === 'SIDEBET_REMOVED') {
              const removedEvent = event as BlackjackEvent & {
                seatPosition: number;
                sidebetType: string;
                refundAmount: number;
              };
              console.log(`[Sidebet] SIDEBET_REMOVED - seat ${removedEvent.seatPosition}, type: ${removedEvent.sidebetType}`);

              // Check if this sidebet is PENDING (not yet committed) or LIVE (committed this hand)
              // chipsPlacedForCountdown = true means chips arrived at icon (LIVE)
              // Also check mySidebets for actual placed bet
              const gameState = useGameStateStore.getState();
              const config = gameState.mySidebetConfigs.get(removedEvent.sidebetType);
              const isLive = config?.chipsPlacedForCountdown === true || gameState.mySidebets.has(removedEvent.sidebetType);

              if (isLive) {
                // LIVE: Bet rides to conclusion - do NOT remove icon or clear bet
                // Let normal SIDEBET_RESOLVED / SIDEBET_CHIPS_COLLECTED path handle it
                console.log(`[Sidebet] SIDEBET_REMOVED - ${removedEvent.sidebetType} is LIVE, rides to conclusion (no visual removal)`);
                // Only clear the config so it won't auto-place next round
                gameState.removeSidebetConfig(removedEvent.sidebetType);
                // Mark for retirement at round boundary - icon will be destroyed at ROUND_COMPLETED
                sidebetsPendingRetirementRef.current.add(removedEvent.sidebetType);
                console.log(`[Sidebet] Marked ${removedEvent.sidebetType} for retirement at round end`);
              } else {
                // PENDING: Clean delete - remove icon and clear config immediately
                console.log(`[Sidebet] SIDEBET_REMOVED - ${removedEvent.sidebetType} is PENDING, clean removal`);
                onSidebetRemovedRef.current?.(removedEvent.seatPosition, removedEvent.sidebetType);
                gameState.removeSidebet(removedEvent.sidebetType);
              }
            }

            // ===========================================
            // ACTION RECOMMENDATION HANDLING (Client-Driven Autoplay / BUDDY!)
            // ===========================================
            // Server sends recommendations for every player turn.
            // Both autoplay and BUDDY! consume the same server recommendations.
            // This is the SINGLE SOURCE OF TRUTH for all recommendations.
            if (event.type === 'ACTION_RECOMMENDATION') {
              const recEvent = event as BlackjackEvent & {
                seatPosition: number;
                handIndex: number;
                recommendedAction: 'HIT' | 'STAND' | 'DOUBLE' | 'SPLIT' | 'SURRENDER';
                reason: string;
                isDeviation?: boolean;
                availableActions: PlayerAction[];
                autoplayDelay?: number; // Server-provided random delay (250-2500ms)
              };

              const gameState = useGameStateStore.getState();
              const isMyEvent = gameState.mySeatPosition === recEvent.seatPosition;

              console.log(`[Recommendation] ACTION_RECOMMENDATION - seat ${recEvent.seatPosition}, action: ${recEvent.recommendedAction}, reason: ${recEvent.reason}${recEvent.isDeviation ? ' (DEVIATION)' : ''}, isMyEvent: ${isMyEvent}`);

              // Store recommendation for BUDDY! to consume (regardless of autoplay setting)
              if (isMyEvent) {
                useGameStateStore.getState().setServerRecommendation(
                  recEvent.recommendedAction,
                  recEvent.reason,
                  recEvent.isDeviation ?? false,
                  recEvent.seatPosition,
                  recEvent.handIndex
                );

                // Check if autoplay is enabled - if so, auto-execute after a delay
                // Delay allows UI to render (neon border, badge) so autoplay
                // is visually indistinguishable from manual play to spectators
                const user = useAuthStore.getState().user;
                const isAutoplayEnabled = user?.autoPlay === true;

                if (isAutoplayEnabled) {
                  // Use server-provided random delay (250-2500ms) for human-like play
                  // Falls back to 800ms if server doesn't provide a delay
                  const autoplayDelay = recEvent.autoplayDelay ?? 800;
                  console.log(`[Recommendation] Autoplay is ON - will execute ${recEvent.recommendedAction} after ${autoplayDelay}ms delay`);

                  setTimeout(() => {
                    // Re-verify autoplay is still enabled and it's still our turn
                    const currentUser = useAuthStore.getState().user;
                    const currentGameState = useGameStateStore.getState();
                    if (currentUser?.autoPlay !== true) {
                      console.log(`[Recommendation] Autoplay was disabled during delay - skipping auto-execute`);
                      return;
                    }
                    if (currentGameState.currentTurnSeat !== recEvent.seatPosition) {
                      console.log(`[Recommendation] Turn changed during delay - skipping auto-execute`);
                      return;
                    }

                    // Log for diagnostics
                    diagLog.action({
                      seat: recEvent.seatPosition,
                      action: recEvent.recommendedAction,
                      source: 'AUTOPLAY',
                    });

                    // Map recommended action to socket message type
                    const actionMap: Record<string, string> = {
                      'HIT': 'hit',
                      'STAND': 'stand',
                      'DOUBLE': 'double',
                      'SPLIT': 'split',
                      'SURRENDER': 'surrender',
                    };

                    const messageType = actionMap[recEvent.recommendedAction];
                    if (messageType) {
                      // Send the action back to the server
                      const currentSocket = useSocketStore.getState().socket;
                      if (currentSocket) {
                        currentSocket.emit('client_message', { type: messageType });
                        console.log(`[Recommendation] Sent ${messageType} to server`);
                      }
                    }
                  }, autoplayDelay);
                } else {
                  console.log(`[Recommendation] Autoplay is OFF - BUDDY! can consume recommendation`);
                }
              }
            }

            // ===========================================
            // INSURANCE RECOMMENDATION HANDLING
            // ===========================================
            if (event.type === 'INSURANCE_RECOMMENDATION') {
              const recEvent = event as BlackjackEvent & {
                seatPosition: number;
                recommendedAction: 'INSURANCE' | 'NO_INSURANCE';
                reason: string;
                isDeviation?: boolean;
              };

              const gameState = useGameStateStore.getState();
              const isMyEvent = gameState.mySeatPosition === recEvent.seatPosition;

              console.log(`[Recommendation] INSURANCE_RECOMMENDATION - seat ${recEvent.seatPosition}, action: ${recEvent.recommendedAction}, reason: ${recEvent.reason}${recEvent.isDeviation ? ' (DEVIATION)' : ''}, isMyEvent: ${isMyEvent}`);

              // Store recommendation for BUDDY! to consume (regardless of autoplay setting)
              if (isMyEvent) {
                useGameStateStore.getState().setServerInsuranceRecommendation(
                  recEvent.recommendedAction,
                  recEvent.reason,
                  recEvent.isDeviation ?? false,
                  recEvent.seatPosition
                );

                // Check if autoplay is enabled - if so, auto-execute after a delay
                // Delay allows UI to render (modal, badge, neon border) so autoplay
                // is visually indistinguishable from manual play to spectators
                const user = useAuthStore.getState().user;
                const isAutoplayEnabled = user?.autoPlay === true;

                if (isAutoplayEnabled) {
                  // Use random delay (1000-3000ms) for human-like insurance decisions
                  // Insurance gets a longer delay than regular actions for visual impact
                  const insuranceDelay = Math.floor(Math.random() * 2001) + 1000; // 1000-3000ms
                  console.log(`[Recommendation] Autoplay is ON - will execute ${recEvent.recommendedAction} after ${insuranceDelay}ms delay`);

                  setTimeout(() => {
                    // Re-verify autoplay is still enabled and we're still in insurance phase
                    const currentUser = useAuthStore.getState().user;
                    const currentGameState = useGameStateStore.getState();
                    if (currentUser?.autoPlay !== true) {
                      console.log(`[Recommendation] Autoplay was disabled during delay - skipping auto-execute`);
                      return;
                    }
                    if (!currentGameState.insuranceOffered) {
                      console.log(`[Recommendation] Insurance phase ended during delay - skipping auto-execute`);
                      return;
                    }

                    // Log for diagnostics
                    diagLog.action({
                      seat: recEvent.seatPosition,
                      action: recEvent.recommendedAction,
                      source: 'AUTOPLAY',
                    });

                    const currentSocket = useSocketStore.getState().socket;
                    if (currentSocket) {
                      if (recEvent.recommendedAction === 'INSURANCE') {
                        // Get the bet amount to calculate insurance amount (half the bet)
                        const player = currentGameState.players.get(recEvent.seatPosition);
                        const bet = player?.hands[0]?.bet ?? 0;
                        const insuranceAmount = Math.floor(bet / 2);
                        currentSocket.emit('client_message', { type: 'insurance', amount: insuranceAmount });
                        console.log(`[Recommendation] Sent insurance (amount: ${insuranceAmount}) to server`);
                      } else {
                        currentSocket.emit('client_message', { type: 'decline_insurance' });
                        console.log(`[Recommendation] Sent decline_insurance to server`);
                      }
                    }
                  }, insuranceDelay);
                } else {
                  console.log(`[Recommendation] Autoplay is OFF - BUDDY! can consume insurance recommendation`);
                }
              }
            }

          } catch (eventErr) {
            // Per-event catch: log but continue processing remaining events.
            // Without this, a single bad event aborts the entire batch,
            // which can leave TURN_STARTED/PLAYER_TURNS_STARTED unprocessed and freeze the game.
            console.error(`[useGameEvents] Error processing event ${event.type} (seq=${event.sequenceNumber}):`, eventErr);
          }
        }
        const postState = useGameStateStore.getState();
        console.log('[useGameEvents DIAG] Processing batch END — turnSignal:', postState.turnSignal, 'availableActions:', JSON.stringify(postState.availableActions), 'currentTurnSeat:', postState.currentTurnSeat, 'lastSeq:', postState.lastEventSequence);
        }; // End of processBatch function

        try {
          // ===========================================
          // ARCHITECTURE SIMPLIFICATION: No timeout
          // Events process to completion. If something hangs, it's a bug to fix.
          // ===========================================
          await processBatch();

          // Clear batch tracking state
          batchProcessingStateRef.current = null;

          // NOTE: We previously requested reconciliation here via join_table,
          // but that caused duplicate event processing because join_table returns
          // table_joined with the full event history for the current round.
          // The client-side guard skipping table_state during batch processing
          // is sufficient - the game_events contain all state changes needed.
        } catch (err) {
          // ===========================================
          // FLAGGED FOR DELETION: Timeout handling
          // With no timeout, BATCH_TIMEOUT errors can't occur.
          // Keep general error handling for unexpected issues.
          // ===========================================
          /*
          if (err instanceof Error && err.message === 'BATCH_TIMEOUT') {
            // BATCH TIMEOUT: Log to admin panel and trigger resync
            const state = batchProcessingStateRef.current;
            console.error('[useGameEvents] BATCH TIMEOUT - batch processing exceeded', BATCH_TIMEOUT_MS, 'ms', state);

            recordBatchTimeout({
              fileName: 'useGameEvents.ts',
              functionName: 'handleGameEvents',
              eventCount: state?.eventCount ?? 0,
              eventTypes: state?.eventTypes ?? [],
              timeoutDuration: BATCH_TIMEOUT_MS,
              processingStartTime: state?.startTime ?? Date.now(),
              lastProcessedEvent: state?.lastProcessedEvent ?? undefined,
              lastProcessedIndex: state?.lastProcessedIndex,
              errorMessage: `Batch timed out after ${BATCH_TIMEOUT_MS}ms. Last processed: ${state?.lastProcessedEvent} (${state?.lastProcessedIndex}/${state?.eventCount})`,
            });

            // Clear batch tracking state
            batchProcessingStateRef.current = null;

            // Trigger a resync to recover visual state
            // The socket will receive a table_joined response with fresh state
            const currentSocket = useSocketStore.getState().socket;
            const currentTableId = useGameStateStore.getState().tableId;
            if (currentSocket && currentTableId) {
              console.log('[useGameEvents] Triggering resync after batch timeout');
              currentSocket.emit('join_table', { tableId: currentTableId });
            }
          } else {
          */
          // Log error but continue - don't let one bad batch block everything
          console.error('[useGameEvents] Batch processing error:', err, (err as Error)?.stack);
          // }

          // Clear batch tracking state
          batchProcessingStateRef.current = null;

          // Reset queue so subsequent batches aren't permanently blocked
          processingQueueRef.current = Promise.resolve();
        }
      }).catch(err => {
        console.error('[useGameEvents] Outer batch processing error:', err, err?.stack);
        // Reset queue so subsequent batches aren't permanently blocked.
        processingQueueRef.current = Promise.resolve();
      });
    },
    [enqueueBatch, processEvent]
  );

  // Subscribe to socket events
  useEffect(() => {
    if (!socket) return;

    // ===========================================
    // REGISTER BATCH HANDLER WITH TABLEMASTER
    // ===========================================
    // ClientTableMaster receives batches from socket, sorts events by
    // ROUND/PHASE/SEQUENCE, filters stale events, then releases sorted
    // batches to this handler. handleGameEvents processes as before.
    const clientTableMaster = getClientTableMaster({ debug: true });

    const handleSortedBatch = (events: BlackjackEvent[], batchKey: string): void => {
      // Pass sorted batch to existing handleGameEvents
      // Events are already ordered by ROUND/PHASE/SEQUENCE
      handleGameEvents({ type: 'game_events', events }, batchKey);
    };

    clientTableMaster.setBatchHandler(handleSortedBatch);
    console.log('[useGameEvents] Registered batch handler with ClientTableMaster');

    // ===========================================
    // SKIP LOG REPORTING - Send to server for analytics
    // ===========================================
    // When ClientTableMaster discards stale events, it records them.
    // This handler sends batched skip logs to the server for pattern analysis.
    clientTableMaster.setSkipLogFlushHandler((skipRecords) => {
      if (socket && skipRecords.length > 0) {
        console.log(`[useGameEvents] Sending ${skipRecords.length} skip records to server`);
        socket.emit('client_skip_log', { records: skipRecords });
      }
    });

    const handleMessage = (message: { type: string; events?: BlackjackEvent[]; table?: any; resyncKey?: number; lastEventSequence?: number }) => {
      // NOTE: We no longer use a dormant check here.
      // With the simplified architecture, the socket stays alive when tab is hidden,
      // and events are processed normally. The server sends lastEventSequence in
      // table_joined to ensure proper sequencing on reconnection.

      console.log('[useGameEvents] Received message:', message.type, message);

      // ===========================================
      // LEGACY HANDLER - MARKED FOR DELETION
      // ===========================================
      // The old 'game_events' message type is no longer sent by the server.
      // All events now come through 'keyed_game_events' via TableMaster.
      // DELETE THIS BLOCK after confirming keyed batch system works correctly.
      // ===========================================
      /*
      if (message.type === 'game_events') {
        handleGameEvents(message);
      }
      */

      // ===========================================
      // KEYED GAME EVENTS - TABLEMASTER ORCHESTRATED
      // ===========================================
      // ClientTableMaster is the WELCOME COMMITTEE - first to receive batches.
      // It unpacks events, sorts by ROUND/PHASE/SEQUENCE, and releases them
      // one-by-one to our handler. We just execute - no batch logic here.
      if (message.type === 'keyed_game_events') {
        const keyedMessage = message as { type: 'keyed_game_events'; batch: KeyedBatch };

        // Get the singleton TableMaster (handler was registered in useEffect)
        const clientTableMaster = getClientTableMaster({ debug: true });

        // Sync state with current game state
        const gameState = useGameStateStore.getState();
        const currentPhase = gameState.phase as GamePhase || GamePhase.WAITING;
        const currentRound = gameState.round?.roundNumber || 0;
        const lastSeq = gameState.lastEventSequence || 0;
        clientTableMaster.syncState(currentRound, currentPhase, lastSeq);

        // Hand batch to TableMaster - it will unpack, order, and release events
        // Events are delivered one-by-one via the registered handler
        clientTableMaster.receiveBatch(keyedMessage.batch);

        // Log summary for debugging
        const summary = clientTableMaster.getSummary();
        console.log('[useGameEvents] ClientTableMaster summary:', summary);
      }

      // ===========================================
      // SINGLE GAME EVENT - DIRECT TO PLAYER (LEGACY)
      // ===========================================
      // NOTE: SCORECARD_UPDATE is now primarily handled in the batch event loop above.
      // This block remains as a fallback for any events sent directly via game_event.
      if (message.type === 'game_event') {
        const singleEventMsg = message as { type: 'game_event'; event: BlackjackEvent };
        const event = singleEventMsg.event;
        console.log(`[useGameEvents] Received single game_event (legacy path): ${event.type}`);

        // Handle SCORECARD_UPDATE - fallback for direct sends (scorecard only, no outcome labels)
        if (event.type === 'SCORECARD_UPDATE') {
          const gameState = useGameStateStore.getState();
          const scorecardEvent = event as BlackjackEvent & {
            seatPosition: number;
            hands: ScorecardHandResult[];
            sidebets: ScorecardSidebetResult[];
            insurance?: ScorecardInsuranceResult;
            totalNetResult: number;
            newBalance: number;
          };
          console.log(`[SCORECARD_UPDATE] Received via legacy game_event: seat=${scorecardEvent.seatPosition}, netResult=${scorecardEvent.totalNetResult}`);

          const isPlayer = scorecardEvent.seatPosition === gameState.mySeatPosition;

          // For player's seat only: call scorecard callback for content update + flip
          // Outcome labels are shown during SEAT_RESOLUTION, synchronized with carousel
          if (isPlayer) {
            onScorecardUpdateRef.current?.(
              scorecardEvent.hands,
              scorecardEvent.sidebets,
              scorecardEvent.insurance,
              scorecardEvent.totalNetResult,
              scorecardEvent.newBalance
            )?.catch(err => console.error('[SCORECARD_UPDATE] Callback error:', err));
          }
        }
      }

      // Handle hand straightened (server-authoritative visual state)
      if (message.type === 'hand_straightened') {
        const straightenMsg = message as { type: 'hand_straightened'; seatPosition: number; handIndex: number };
        const gameState = useGameStateStore.getState();
        const isOpponent = gameState.mySeatPosition !== straightenMsg.seatPosition;

        // Log received hand_straightened event
        diagLog.recv({
          seq: -1,
          type: 'HAND_STRAIGHTENED',
          seat: straightenMsg.seatPosition,
          hand: straightenMsg.handIndex,
          details: { isOpponent }
        });

        if (isOpponent) {
          // PIXI PURITY: Opponent hand straightening via Pixi callback (no DOM fallback)
          if (onOpponentHandStraightenedRef.current) {
            console.log(`[useGameEvents] Calling onOpponentHandStraightened for seat=${straightenMsg.seatPosition}, handIndex=${straightenMsg.handIndex}`);
            // Fire and forget, but catch errors to prevent silent failures
            onOpponentHandStraightenedRef.current(straightenMsg.seatPosition, straightenMsg.handIndex)
              .catch(err => console.error('[useGameEvents] onOpponentHandStraightened error:', err));
          } else {
            console.warn(`[useGameEvents] No onOpponentHandStraightened callback for seat ${straightenMsg.seatPosition}`);
          }
        } else {
          console.log(`[useGameEvents] Ignoring hand_straightened for own seat ${straightenMsg.seatPosition}`);
        }
        // For current user, the local straightening already happened when they tapped
      }

      // Also handle table state for initial sync
      // CRITICAL: Skip table_state during batch processing to prevent animations racing
      // The server may be rounds ahead; table_state would instantly jump the UI state
      // while game_events are still animating. We reconcile after batch completes.
      if (message.type === 'table_state' && batchProcessingStateRef.current) {
        console.log(`[useGameEvents] SKIPPING table_state - batch processing active (${batchProcessingStateRef.current})`);
        return;
      }

      if (message.type === 'table_joined' || message.type === 'table_state') {
        // ===========================================
        // RESET ClientTableMaster ON TABLE JOIN
        // ===========================================
        // When joining a new table, reset the ClientTableMaster's stale event tracking.
        // This prevents events from the new table being discarded as "stale" because
        // lastProcessedRound is still set from a previous table (e.g., round 5 from
        // old table causes round 1 events from new table to be discarded).
        if (message.type === 'table_joined') {
          const clientTableMaster = getClientTableMaster({ debug: true });
          clientTableMaster.resetForNewTable();
          console.log('[useGameEvents] Reset ClientTableMaster for new table join');
        }

        // CRITICAL: Sync lastEventSequence from server on reconnection
        // The server now sends lastEventSequence in table_joined to indicate
        // which sequence number corresponds to the snapshot state.
        // Setting this ensures the client only processes events AFTER the snapshot,
        // preventing visual chaos from duplicate/stale event processing.
        if (message.type === 'table_joined' && typeof message.lastEventSequence === 'number') {
          const oldSeq = useGameStateStore.getState().lastEventSequence;
          console.log(`[useGameEvents] Setting lastEventSequence from server: ${message.lastEventSequence} (was ${oldSeq})`);
          useGameStateStore.setState({ lastEventSequence: message.lastEventSequence });
        }

        if (message.table) {
          // Map backend phase string to GamePhase enum
          // Backend phases: waiting, betting, dealing, insurance, player_turn, dealer_turn, payout, complete
          // Note: 'complete' maps to BETTING because the backend accepts bets after round completion
          const phaseMap: Record<string, GamePhase> = {
            'waiting': GamePhase.WAITING,
            'betting': GamePhase.BETTING,
            'dealing': GamePhase.DEALING,
            'insurance': GamePhase.DEALING, // Insurance is NOT a betting phase - don't let autobet fire
            'player_turn': GamePhase.PLAYING,
            'dealer_turn': GamePhase.DEALER_TURN,
            'payout': GamePhase.RESOLVING,
            'complete': GamePhase.BETTING, // Round done - ready for new bets
          };
          const phase = phaseMap[message.table.phase] || GamePhase.WAITING;

          // DEBUG: Log card counts in table_state to detect stale cards
          const seatCardCounts: Record<number, number> = {};
          if (message.table.seats) {
            for (const seat of message.table.seats) {
              if (seat?.hands) {
                const totalCards = seat.hands.reduce((sum: number, h: { cards?: unknown[] }) => sum + (h.cards?.length || 0), 0);
                if (totalCards > 0) {
                  seatCardCounts[seat.seatIndex + 1] = totalCards;
                }
              }
            }
          }
          const dealerCardCount = message.table.dealer?.cards?.length || 0;
          console.log(`[useGameEvents DEBUG] ${message.type} | phase=${message.table.phase} | seatCards=${JSON.stringify(seatCardCounts)} | dealerCards=${dealerCardCount}`);

          // NOTE: Chip clone capture moved to game_events handler only.
          // The old animation system (chipAnimations.ts) doesn't use unified clones,
          // so capturing here would create orphaned clones that persist after animation.

          setTable(
            message.table.id,
            message.table.name,
            message.table.minBalance ?? 0,
            phase
          );

          // Set dealer persona if available
          if (message.table.dealerPersona) {
            setDealerPersona(message.table.dealerPersona);
          }

          // Set penetration info and detect shuffle
          if (message.table.penetration !== undefined) {
            const newCardsDealt = message.table.cardsDealt ?? 0;
            const currentCardsDealt = useCardTrackingStore.getState().cardsSeenSinceShuffle.length;

            // Detect shuffle: if server reports fewer cards than we've tracked,
            // a shuffle has occurred
            if (newCardsDealt < currentCardsDealt * 0.5 && currentCardsDealt > 10) {
              console.log('[CardTracking] Shuffle detected - resetting count');
              useCardTrackingStore.getState().resetOnShuffle();
            }

            setPenetration(newCardsDealt, message.table.penetration, message.table.reshufflePending ?? false);
            // Sync card tracking store with server penetration for HUD accuracy
            useCardTrackingStore.getState().syncServerPenetration(message.table.penetration);
          }

          // Populate players from seats (so usernames are visible before game events)
          if (message.table.seats) {
            // Get current occupied seats before update
            const currentOccupied = useGameStateStore.getState().occupiedSeats;

            // Calculate which seats will be occupied after update
            const newOccupied = new Set<number>();
            for (const seat of message.table.seats) {
              if (seat) {
                newOccupied.add(seat.seatIndex + 1); // Convert 0-based to 1-based
              }
            }

            // PIXI PURITY: DOM clone cleanup removed - Pixi manages card sprites internally
            // When a player leaves, Pixi's clearTable() or seat-specific cleanup handles sprites
            for (const seatPos of currentOccupied) {
              if (!newOccupied.has(seatPos)) {
                console.log('[useGameEvents] Player left seat', seatPos, '- Pixi will handle sprite cleanup');
                // clearStoredCards() and clearArrivalClonesForSeat() removed - not needed for Pixi
              }
            }

            // Only sync full game state on INITIAL join (table_joined), not on
            // subsequent table_state broadcasts. table_state is broadcast after
            // every action (hit, stand, etc.) and we don't want to overwrite
            // the event-driven state updates.
            if (message.type === 'table_joined') {
              // DEBUG: Log what server sent for reconnection diagnosis
              const serverPhase = message.table.phase;
              const serverDealerCards = message.table.dealer?.cards?.length || 0;
              const serverSeatHands: Record<number, { handsCount: number; cardsTotal: number }> = {};
              message.table.seats.forEach((seat: { seatIndex?: number; hands?: Array<{ cards?: unknown[] }> } | null) => {
                if (seat?.hands && seat.hands.length > 0) {
                  const cardsTotal = seat.hands.reduce((sum: number, h: { cards?: unknown[] }) => sum + (h.cards?.length || 0), 0);
                  serverSeatHands[seat.seatIndex ?? -1] = { handsCount: seat.hands.length, cardsTotal };
                }
              });
              console.log('[useGameEvents] table_joined debug:', {
                phase: serverPhase,
                dealerCards: serverDealerCards,
                seatHands: serverSeatHands,
              });

              // Check if there's an active game (cards dealt) that we should sync
              // This handles spectators joining mid-hand
              const hasActiveGame = message.table.seats.some(
                (seat: { hands?: Array<{ cards?: unknown[] }> } | null) =>
                  seat?.hands && seat.hands.length > 0 && seat.hands.some(h => h.cards && h.cards.length > 0)
              ) || (message.table.dealer?.cards?.length > 0);

              console.log('[useGameEvents] hasActiveGame:', hasActiveGame);

              if (hasActiveGame) {
                // Sync full game state including hands and dealer
                console.log('[useGameEvents] Initial join with active game, syncing full state');
                // availableActions can come from table.availableActions OR turnInfo.availableActions
                const turnInfo = (message as { turnInfo?: { availableActions?: string[] } }).turnInfo;
                const availableActions = message.table.availableActions
                  || turnInfo?.availableActions
                  || [];
                console.log('[useGameEvents] availableActions from server:', availableActions, 'turnInfo:', turnInfo);
                syncFromTableState({
                  seats: message.table.seats,
                  dealer: message.table.dealer || { cards: [] },
                  currentSeatIndex: message.table.currentSeatIndex ?? -1,
                  buttonSeatIndex: message.table.buttonSeatIndex ?? -1,
                  availableActions,
                });

                // Set mySeatPosition from table_joined seats
                // This is critical for staged bet box, scoreboxes, and outcome labels to render
                const currentUserId = useAuthStore.getState().user?.id;
                if (currentUserId) {
                  const mySeat = message.table.seats.find((seat: { userId?: string } | null) => seat?.userId === currentUserId);
                  if (mySeat) {
                    const mySeatPosition = (mySeat as { seatIndex: number }).seatIndex + 1; // Convert 0-based to 1-based
                    setMySeat(mySeatPosition);
                    console.log(`[useGameEvents] Set mySeatPosition to ${mySeatPosition} from table_joined`);

                    // Restore sidebet configs from server (for autobet persistence across refreshes)
                    const seatWithConfigs = mySeat as { lastSidebetConfigs?: Array<{ sidebetType: string; percentage: number; rounds: number }> };
                    if (seatWithConfigs.lastSidebetConfigs && seatWithConfigs.lastSidebetConfigs.length > 0) {
                      console.log(`[useGameEvents] Restoring ${seatWithConfigs.lastSidebetConfigs.length} sidebet configs from server`);
                      const newConfigs = new Map<string, { percentage: number; rounds: number; chipsPlacedForCountdown: boolean }>();
                      for (const config of seatWithConfigs.lastSidebetConfigs) {
                        newConfigs.set(config.sidebetType, {
                          percentage: config.percentage,
                          rounds: config.rounds,
                          chipsPlacedForCountdown: false,
                        });
                      }
                      useGameStateStore.setState({ mySidebetConfigs: newConfigs });
                    }
                  } else {
                    // User is not seated (spectator) - clear mySeatPosition
                    setMySeat(null);
                    console.log('[useGameEvents] User not seated (spectator) - cleared mySeatPosition');
                  }
                }

                // Restore insurance state if joining during insurance phase
                if (message.table.insuranceOffered) {
                  console.log('[useGameEvents] Restoring insuranceOffered state from table_joined, deadline:', message.table.insuranceDeadline);
                  useGameStateStore.setState({
                    insuranceOffered: true,
                    insuranceDeadline: message.table.insuranceDeadline || null,
                  });
                }

                // Sync dealt card counts and scores for PixiJS scoreboxes and control panel
                // Extract card counts and scores per seat/hand from the table state
                const seatCardCounts: Record<number, number[]> = {};
                const seatScores: Record<number, Array<{ score: number; isSoft: boolean }>> = {};
                message.table.seats.forEach((seat: { hands?: Array<{ cards?: unknown[] }> } | null, index: number) => {
                  if (seat?.hands && seat.hands.length > 0) {
                    const handCounts = seat.hands.map(h => h.cards?.length ?? 0);
                    if (handCounts.some(c => c > 0)) {
                      const seatPosition = index + 1; // Seat positions are 1-indexed
                      seatCardCounts[seatPosition] = handCounts;
                      // Get scores from Zustand store (syncFromTableState has already populated them)
                      const player = useGameStateStore.getState().players.get(seatPosition);
                      if (player?.hands) {
                        seatScores[seatPosition] = player.hands.map(h => ({ score: h.score, isSoft: h.isSoft }));
                      }
                    }
                  }
                });
                const dealerCardCount = message.table.dealer?.cards?.length ?? 0;
                // Get dealer score from Zustand store
                const dealerState = useGameStateStore.getState().dealer;
                const dealerScore = dealerState.visibleScore !== undefined
                  ? { score: dealerState.visibleScore, isSoft: dealerState.isSoft }
                  : undefined;
                onSyncDealtCountsRef.current?.(seatCardCounts, dealerCardCount, seatScores, dealerScore);

                // ALWAYS trigger fullVisualResync on table_joined with active game.
                // This handles:
                //   - Keyed resync (old tab-switch flow)
                //   - Lifecycle registry resync (new flow - clears state before resync, so state is fresh)
                //   - Fresh join with active game (spectator joining mid-hand)
                // The reconciliation logic has card creation DISABLED (stale state concerns),
                // so fullVisualResync is the only way to rebuild visuals from scratch.
                console.log('[useGameEvents] table_joined with active game - triggering fullVisualResync');
                requestAnimationFrame(() => {
                  onKeyedResyncAcceptedRef.current?.();
                });

                // PIXI PURITY: DOM clone health-check removed - Pixi manages card sprites via fullVisualResync
                // ensureAllClonesExist() and hideAllReactCardsWithClones() no longer needed
                // onKeyedResyncAccepted callback above handles Pixi scene rebuild
              } else {
                // No active game in current round, but check for lastRound data
                // (preserved from previous round for visual display during betting phase)
                const lastRoundHands = message.table.lastRoundHands as Record<number, Array<{
                  cards: Array<{ rank: string; suit: string; faceUp: boolean }>;
                  bet: number;
                  betChips?: number[];
                  result?: string;
                  isBlackjack?: boolean;
                  isBusted?: boolean;
                  isDoubled?: boolean;
                }>> | null;
                const lastRoundDealerCards = message.table.lastRoundDealerCards as Array<{ rank: string; suit: string; faceUp: boolean }> | null;

                if (lastRoundHands && Object.keys(lastRoundHands).length > 0) {
                  // We have lastRound data - construct seats with this data for visual display
                  console.log('[useGameEvents] Using lastRoundHands for visual display during betting phase');

                  // Build seats array with lastRound hands
                  const seatsWithLastRound = message.table.seats.map((seat: {
                    seatIndex: number;
                    userId: string;
                    username: string;
                    avatarUrl?: string | null;
                    hands?: Array<{ cards?: unknown[]; bet?: number }>;
                    currentHandIndex?: number;
                    totalBet?: number;
                    isActive?: boolean;
                    isTurn?: boolean;
                    insuranceBet?: number;
                    insuranceDeclined?: boolean;
                  } | null) => {
                    if (!seat) return null;
                    const lastHands = lastRoundHands[seat.seatIndex];
                    if (lastHands && lastHands.length > 0) {
                      return {
                        ...seat,
                        hands: lastHands,
                        currentHandIndex: 0,
                        // IMPORTANT: Set totalBet to 0 because we're in a NEW betting round.
                        // The lastRound cards are displayed for visual continuity only.
                        // Setting totalBet would block the BET button (confirmedBet != 0).
                        totalBet: 0,
                        isActive: false, // Not active yet - waiting for new bet
                        isTurn: false,
                        insuranceBet: 0,
                        insuranceDeclined: false,
                      };
                    }
                    return seat;
                  });

                  // Build dealer state with lastRound cards
                  const dealerWithLastRound = lastRoundDealerCards && lastRoundDealerCards.length > 0
                    ? { cards: lastRoundDealerCards.map(c => ({ ...c, faceUp: true })) }
                    : { cards: [] };

                  // Suppress label fly-in and outcome SFX when restoring lastRound display
                  // These outcomes already played during the original round
                  useAnimationQueueStore.getState().setSuppressLabelFlyIn(true);
                  useAnimationQueueStore.getState().setSuppressOutcomeSfx(true);

                  syncFromTableState({
                    seats: seatsWithLastRound,
                    dealer: dealerWithLastRound,
                    currentSeatIndex: -1,
                    buttonSeatIndex: message.table.buttonSeatIndex ?? -1,
                    availableActions: [],
                  });

                  // Set mySeatPosition from table_joined seats (lastRound case)
                  const currentUserId = useAuthStore.getState().user?.id;
                  if (currentUserId) {
                    const mySeat = message.table.seats.find((seat: { userId?: string } | null) => seat?.userId === currentUserId);
                    if (mySeat) {
                      const mySeatPosition = (mySeat as { seatIndex: number }).seatIndex + 1;
                      setMySeat(mySeatPosition);
                      console.log(`[useGameEvents] Set mySeatPosition to ${mySeatPosition} from table_joined (lastRound)`);

                      // Restore sidebet configs from server (for autobet persistence across refreshes)
                      const seatWithConfigs = mySeat as { lastSidebetConfigs?: Array<{ sidebetType: string; percentage: number; rounds: number }> };
                      if (seatWithConfigs.lastSidebetConfigs && seatWithConfigs.lastSidebetConfigs.length > 0) {
                        console.log(`[useGameEvents] Restoring ${seatWithConfigs.lastSidebetConfigs.length} sidebet configs from server (lastRound)`);
                        const newConfigs = new Map<string, { percentage: number; rounds: number; chipsPlacedForCountdown: boolean }>();
                        for (const config of seatWithConfigs.lastSidebetConfigs) {
                          newConfigs.set(config.sidebetType, {
                            percentage: config.percentage,
                            rounds: config.rounds,
                            chipsPlacedForCountdown: false,
                          });
                        }
                        useGameStateStore.setState({ mySidebetConfigs: newConfigs });
                      }
                    } else {
                      setMySeat(null);
                      console.log('[useGameEvents] User not seated (spectator) - cleared mySeatPosition (lastRound)');
                    }
                  }

                  // Reset suppression after components have mounted
                  requestAnimationFrame(() => {
                    useAnimationQueueStore.getState().setSuppressLabelFlyIn(false);
                    useAnimationQueueStore.getState().setSuppressOutcomeSfx(false);
                  });

                  // Sync dealt card counts for PixiJS display
                  const seatCardCounts: Record<number, number[]> = {};
                  const seatScores: Record<number, Array<{ score: number; isSoft: boolean }>> = {};
                  for (const [seatIndexStr, hands] of Object.entries(lastRoundHands)) {
                    const seatIndex = parseInt(seatIndexStr, 10);
                    const seatPosition = seatIndex + 1;
                    seatCardCounts[seatPosition] = hands.map(h => h.cards?.length ?? 0);
                    const player = useGameStateStore.getState().players.get(seatPosition);
                    if (player?.hands) {
                      seatScores[seatPosition] = player.hands.map(h => ({ score: h.score, isSoft: h.isSoft }));
                    }
                  }
                  const dealerCardCount = lastRoundDealerCards?.length ?? 0;
                  const dealerState = useGameStateStore.getState().dealer;
                  const dealerScore = dealerState.visibleScore !== undefined
                    ? { score: dealerState.visibleScore, isSoft: dealerState.isSoft }
                    : undefined;
                  onSyncDealtCountsRef.current?.(seatCardCounts, dealerCardCount, seatScores, dealerScore);

                  // ALWAYS trigger fullVisualResync on table_joined with lastRound data
                  // (same reason as active game case - lifecycle registry needs full rebuild)
                  console.log('[useGameEvents] table_joined (lastRound) - triggering fullVisualResync');
                  requestAnimationFrame(() => {
                    onKeyedResyncAcceptedRef.current?.();
                  });

                  // PIXI PURITY: DOM clone health-check removed - Pixi manages card sprites via fullVisualResync
                  // ensureAllClonesExist() and hideAllReactCardsWithClones() no longer needed
                } else {
                  // No lastRound data, just set seated players (username/avatar only)
                  console.log('[useGameEvents] No active game (hasActiveGame=false), only setting seated players');
                  setSeatedPlayers(message.table.seats);

                  // Set mySeatPosition from table_joined seats (no active game case)
                  const currentUserId = useAuthStore.getState().user?.id;
                  if (currentUserId) {
                    const mySeat = message.table.seats.find((seat: { userId?: string } | null) => seat?.userId === currentUserId);
                    if (mySeat) {
                      const mySeatPosition = (mySeat as { seatIndex: number }).seatIndex + 1;
                      setMySeat(mySeatPosition);
                      console.log(`[useGameEvents] Set mySeatPosition to ${mySeatPosition} from table_joined (no active game)`);

                      // Restore sidebet configs from server (for autobet persistence across refreshes)
                      const seatWithConfigs = mySeat as { lastSidebetConfigs?: Array<{ sidebetType: string; percentage: number; rounds: number }> };
                      if (seatWithConfigs.lastSidebetConfigs && seatWithConfigs.lastSidebetConfigs.length > 0) {
                        console.log(`[useGameEvents] Restoring ${seatWithConfigs.lastSidebetConfigs.length} sidebet configs from server (no active game)`);
                        const newConfigs = new Map<string, { percentage: number; rounds: number; chipsPlacedForCountdown: boolean }>();
                        for (const config of seatWithConfigs.lastSidebetConfigs) {
                          newConfigs.set(config.sidebetType, {
                            percentage: config.percentage,
                            rounds: config.rounds,
                            chipsPlacedForCountdown: false,
                          });
                        }
                        useGameStateStore.setState({ mySidebetConfigs: newConfigs });
                      }
                    } else {
                      setMySeat(null);
                      console.log('[useGameEvents] User not seated (spectator) - cleared mySeatPosition (no active game)');
                    }
                  }

                  // ALWAYS call fullVisualResync on table_joined (even with no cards)
                  // This ensures Pixi scene is in consistent state after lifecycle registry resync
                  console.log('[useGameEvents] table_joined (no cards) - triggering fullVisualResync for consistency');
                  requestAnimationFrame(() => {
                    onKeyedResyncAcceptedRef.current?.();
                  });
                }
              }
            } else {
              // table_state broadcast - only update seated players (username/avatar)
              // Cards and game state are handled by game events
              setSeatedPlayers(message.table.seats);
            }
          }
        }
      }
    };

    socket.on('server_message', handleMessage);

    return () => {
      socket.off('server_message', handleMessage);
    };
  }, [socket, handleGameEvents, setTable, setDealerPersona, setPenetration, setSeatedPlayers, syncFromTableState, setMySeat]);

  return {
    isConnected: !!socket,
    tableId,
  };
}

export default useGameEvents;
